import { Injectable, signal } from '@angular/core';

export type Language = 'FR' | 'EN';

type Translations = {
    [key: string]: string;
};

@Injectable({
    providedIn: 'root'
})
export class LanguageService {
    currentLang = signal<Language>('FR');

    private translations: { FR: Translations; EN: Translations } = {
        FR: {
            'stock.orderDetails': 'Détails stock',
            'sidebar.dashboard': 'Dashboard',
            'sidebar.dashboardAdmin': 'Tableau de bord Admin',
            'sidebar.dashboardStudy': 'Tableau de bord Études',
            'sidebar.users': 'Utilisateurs',
            'sidebar.subscriptions': 'Plans d\'abonnement',
            'sidebar.subcontractors': 'Sous-traitants',
            'sidebar.suppliers': 'Fournisseurs',
            'sidebar.settings': 'Paramètres',
            'sidebar.unitMeasure': 'Unités de mesure',
            'sidebar.documents': 'Documents',
            'sidebar.propertyType': 'Types de propriété',
            'sidebar.materialCategories': 'Catégories de matériaux',
            'sidebar.projects': 'Projets',
            'sidebar.requests': 'Demandes',
            'sidebar.orders': 'Commandes',
            'sidebar.myAccount': 'Mon compte',
            'sidebar.logout': 'Déconnexion',
            'sidebar.reclamations': 'Réclamations',
            'breadcrumb.helpSupport': 'Aide & Support',
            'breadcrumb.language': 'Langue',
            'breadcrumb.home': 'Accueil',
            'breadcrumb.dashboard': 'Tableau de bord',

            // ============ AUTHENTICATION ============
            // Login Page
            'auth.login.title': 'Bienvenue sur UKUBHALISA.',
            'auth.login.subtitle': 'Système de pointage et de gestion des chantiers simplifié !',
            'auth.login.emailLabel': 'Identifiant ou Adresse email',
            'auth.login.emailPlaceholder': 'Entrez votre identifiant',
            'auth.login.passwordLabel': 'Mot de passe',
            'auth.login.passwordPlaceholder': 'Entrez votre mot de passe (6 caractères min)',
            'auth.login.showPassword': 'Afficher le mot de passe',
            'auth.login.hidePassword': 'Masquer le mot de passe',
            'auth.login.forgotPassword': 'Mot de passe oublié ?',
            'auth.login.createAccount': 'Créer un compte',
            'auth.login.loginButton': 'Se connecter',
            'auth.login.loggingIn': 'Connexion en cours...',
            'auth.login.backToHome': 'Retour à l\'accueil',
            'auth.login.selectLanguage': 'Sélectionner la langue',
            'Rechercher par matériel, unité...': 'Rechercher par matériel, unité...',
            'stock.filters.searchPlaceholder': 'Rechercher par matériel, unité...',

            // Login Errors
            'auth.login.error.required': 'L\'email ou le numéro de téléphone est requis',
            'auth.login.error.invalidFormat': 'Format invalide. Utilisez un email valide ou un numéro au format 7XXXXXXXX (ex: 771234567)',
            'auth.login.error.passwordRequired': 'Le mot de passe est requis',
            'auth.login.error.passwordMinLength': 'Le mot de passe doit contenir au moins 6 caractères',
            'auth.login.error.invalidCredentials': 'Email/téléphone ou mot de passe incorrect',
            'auth.login.error.serverError': 'Erreur serveur. Veuillez réessayer plus tard.',
            'auth.login.error.networkError': 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.',
            'auth.login.error.accessDenied': 'Accès refusé. Votre compte pourrait être suspendu.',
            'auth.login.error.formErrors': 'Veuillez corriger les erreurs dans le formulaire',

            // Login Success
            'auth.login.success.welcome': 'Connexion réussie ! Bienvenue sur votre espace.',
            'auth.login.success.welcomeAdmin': 'Connexion réussie ! Bienvenue sur votre espace administrateur.',
            'auth.login.success.welcomeBET': 'Connexion réussie ! Bienvenue sur votre espace BET.',
            'auth.login.success.welcomeSupplier': 'Connexion réussie ! Bienvenue sur votre espace fournisseur.',
            'auth.login.warning.noSubscription': 'Veuillez souscrire à un abonnement pour accéder au dashboard.',
            'auth.login.error.subscriptionCheck': 'Erreur lors de la vérification de votre abonnement. Veuillez réessayer.',

            // Register Page
            'auth.register.title': 'Créer un compte',
            'auth.register.subtitle': 'Rejoignez UKUBHALISA et gérez vos chantiers efficacement',
            'auth.register.firstNameLabel': 'Prénom',
            'auth.register.firstNamePlaceholder': 'Entrez votre prénom',
            'auth.register.lastNameLabel': 'Nom',
            'auth.register.lastNamePlaceholder': 'Entrez votre nom',
            'auth.register.emailLabel': 'Adresse email',
            'auth.register.emailPlaceholder': 'exemple@email.com',
            'auth.register.phoneLabel': 'Numéro de téléphone',
            'auth.register.phonePlaceholder': '771234567',
            'auth.register.passwordLabel': 'Mot de passe',
            'auth.register.passwordPlaceholder': 'Minimum 6 caractères',
            'auth.register.confirmPasswordLabel': 'Confirmer le mot de passe',
            'auth.register.confirmPasswordPlaceholder': 'Retapez votre mot de passe',
            'auth.register.profileLabel': 'Profil',
            'auth.register.profilePlaceholder': 'Sélectionnez votre profil',
            'auth.register.companyLabel': 'Entreprise',
            'auth.register.companyPlaceholder': 'Nom de votre entreprise',
            'auth.register.addressLabel': 'Adresse',
            'auth.register.addressPlaceholder': 'Votre adresse complète',
            'auth.register.registerButton': 'S\'inscrire',
            'auth.register.registering': 'Inscription en cours...',
            'auth.register.alreadyHaveAccount': 'Vous avez déjà un compte ?',
            'auth.register.loginLink': 'Se connecter',
            'auth.register.termsAccept': 'J\'accepte les conditions d\'utilisation',

            // Register Errors
            'auth.register.error.firstNameRequired': 'Le prénom est requis',
            'auth.register.error.lastNameRequired': 'Le nom est requis',
            'auth.register.error.emailRequired': 'L\'email est requis',
            'auth.register.error.emailInvalid': 'Format d\'email invalide',
            'auth.register.error.phoneRequired': 'Le numéro de téléphone est requis',
            'auth.register.error.phoneInvalid': 'Format de téléphone invalide (ex: 771234567)',
            'auth.register.error.passwordRequired': 'Le mot de passe est requis',
            'auth.register.error.passwordMinLength': 'Le mot de passe doit contenir au moins 6 caractères',
            'auth.register.error.confirmPasswordRequired': 'Veuillez confirmer votre mot de passe',
            'auth.register.error.passwordMismatch': 'Les mots de passe ne correspondent pas',
            'auth.register.error.profileRequired': 'Veuillez sélectionner un profil',
            'auth.register.error.termsRequired': 'Vous devez accepter les conditions d\'utilisation',
            'auth.register.error.emailExists': 'Cet email est déjà utilisé',
            'auth.register.error.phoneExists': 'Ce numéro de téléphone est déjà utilisé',

            // Register Success
            'auth.register.success.accountCreated': 'Compte créé avec succès !',
            'auth.register.success.redirecting': 'Redirection vers la page de connexion...',

            // Reset Password
            'auth.reset.title': 'Réinitialiser le mot de passe',
            'auth.reset.subtitle': 'Entrez votre email pour recevoir un lien de réinitialisation',
            'auth.reset.emailLabel': 'Adresse email',
            'auth.reset.emailPlaceholder': 'exemple@email.com',
            'auth.reset.sendButton': 'Envoyer le lien',
            'auth.reset.sending': 'Envoi en cours...',
            'auth.reset.backToLogin': 'Retour à la connexion',
            'auth.reset.success': 'Un email de réinitialisation a été envoyé',
            'auth.reset.error.emailRequired': 'L\'email est requis',
            'auth.reset.error.emailInvalid': 'Format d\'email invalide',
            'auth.reset.error.emailNotFound': 'Aucun compte associé à cet email',
            'auth.reset.error.serverError': 'Erreur lors de l\'envoi de l\'email',

            'profile.siteManager': 'Manager',
            'profile.subcontractor': 'Sous-traitant',
            'profile.supplier': 'Fournisseur',
            'profile.admin': 'Administrateur',
            'profile.bet': 'Bureau d\'études',
            'profile.user': 'Utilisateur',
            'profile.promoter': 'Promoteur',
            'profile.moa': 'Maître d\'ouvrage',
            'profile.notaire': 'Notaire',
            'profile.reservataire': 'Réservataire',
            'profile.bank': 'Banque',
            'profile.agency': 'Agence',
            'profile.proprietaire': 'Propriétaire',
            'profile.syndic': 'Syndic',
            'profile.locataire': 'Locataire',
            'profile.prestataire': 'Prestataire',
            'profile.tom': 'TOM',
            'profile.worker': 'Salarié',

            // ============ ADMIN PAGES ============
            // Subscription Plans
            'subscriptions.plansTitle': 'Plans d\'abonnement',
            'subscriptions.createPlan': 'Créer un plan',
            'subscriptions.editPlan': 'Modifier le plan',
            'subscriptions.deletePlan': 'Supprimer le plan',
            'subscriptions.planName': 'Nom du plan',
            'subscriptions.monthlyPrice': 'Prix mensuel',
            'subscriptions.yearlyPrice': 'Prix annuel',
            'subscriptions.features': 'Fonctionnalités',
            'subscriptions.table.name': 'Nom',
            'subscriptions.table.price': 'Prix',
            'subscriptions.table.status': 'Statut',
            'subscriptions.table.actions': 'Actions',
            'subscriptions.success.created': 'Plan créé avec succès',
            'subscriptions.success.updated': 'Plan mis à jour avec succès',
            'subscriptions.success.deleted': 'Plan supprimé avec succès',

            // Subcontractors
            'subcontractors.title': 'Gestion des sous-traitants',
            'subcontractors.addSubcontractor': 'Ajouter un sous-traitant',
            'subcontractors.companyName': 'Nom de l\'entreprise',
            'subcontractors.contactName': 'Nom du contact',
            'subcontractors.email': 'Email',
            'subcontractors.phone': 'Téléphone',
            'subcontractors.address': 'Adresse',
            'subcontractors.specialty': 'Spécialité',
            'subcontractors.table.company': 'Entreprise',
            'subcontractors.table.contact': 'Contact',
            'subcontractors.table.specialty': 'Spécialité',
            'subcontractors.table.actions': 'Actions',
            'subcontractors.success.created': 'Sous-traitant ajouté avec succès',

            // Suppliers
            'suppliers.title': 'Gestion des fournisseurs',
            'suppliers.addSupplier': 'Ajouter un fournisseur',
            'suppliers.companyName': 'Nom de l\'entreprise',
            'suppliers.contactName': 'Nom du contact',
            'suppliers.email': 'Email',
            'suppliers.phone': 'Téléphone',
            'suppliers.category': 'Catégorie',
            'suppliers.table.company': 'Entreprise',
            'suppliers.table.contact': 'Contact',
            'suppliers.table.category': 'Catégorie',
            'suppliers.table.actions': 'Actions',
            'suppliers.success.created': 'Fournisseur ajouté avec succès',

            // Supplier Dashboard
            'supplier.dashboard.title': 'Tableau de bord Fournisseur',
            'supplier.dashboard.refresh': 'Actualiser',
            'supplier.dashboard.stats.pending': 'En attente',
            'supplier.dashboard.stats.approved': 'Approuvées',
            'supplier.dashboard.stats.inDelivery': 'En livraison',
            'supplier.dashboard.stats.delivered': 'Livrées',
            'supplier.dashboard.stats.rejected': 'Rejetées',
            'supplier.dashboard.charts.distribution': 'Répartition des commandes (%)',
            'supplier.dashboard.charts.topProperties': 'Top Propriétés',
            'supplier.dashboard.period.thisMonth': 'Ce mois',
            'supplier.dashboard.period.thisQuarter': 'Ce trimestre',
            'supplier.dashboard.period.thisYear': 'Cette année',
            'supplier.dashboard.noData': 'Aucune donnée disponible',
            'supplier.dashboard.error': 'Erreur lors du chargement des données',

            // Supplier Orders
            'supplier.orders.title': 'Mes commandes',
            'supplier.orders.createQuote': 'Créer une citation',
            'supplier.orders.viewDetails': 'Voir détails',
            'supplier.orders.orderNumber': 'N° Commande',
            'supplier.orders.date': 'Date',
            'supplier.orders.property': 'Propriété',
            'supplier.orders.status': 'Statut',
            'supplier.orders.actions': 'Actions',
            'supplier.orders.noOrders': 'Aucune commande disponible',

            // Settings
            'settings.title': 'Paramètres',
            'settings.units.title': 'Unités de mesure',
            'settings.units.add': 'Ajouter une unité',
            'settings.units.name': 'Nom de l\'unité',
            'settings.units.symbol': 'Symbole',
            'settings.documents.title': 'Types de documents',
            'settings.propertyTypes.title': 'Types de propriété',
            'settings.materialCategories.title': 'Catégories de matériaux',

            'common.search': 'Rechercher',
            'common.searchPlaceholder': 'Rechercher...',
            'common.filter': 'Filtres',
            'common.export': 'Exporter',
            'common.add': 'Ajouter',
            'common.edit': 'Modifier',
            'common.delete': 'Supprimer',
            'common.save': 'Enregistrer', // Unique key
            'common.cancel': 'Annuler',
            'common.close': 'Fermer',
            'common.next': 'Suivant',
            'common.previous': 'Précédent',
            'common.loading': 'Chargement...',
            'common.noData': 'Aucune donnée disponible',
            'common.actions': 'Actions',
            'common.status': 'Statut',
            'common.date': 'Date',
            'common.view': 'Voir',
            'common.copy': 'Copier',
            'common.confirm': 'Confirmer',
            'common.yes': 'Oui',
            'common.no': 'Non',
            'common.active': 'Actif',
            'common.inactive': 'Inactif',
            'common.all': 'Tous',
            'common.create': 'Créer',
            'common.update': 'Mettre à jour',
            'common.download': 'Télécharger',
            'common.upload': 'Téléverser',
            'common.back': 'Retour',
            'common.accept': 'Accepter',
            'common.reject': 'Rejeter',
            'common.validate': 'Valider',
            'common.refuse': 'Refuser',
            'common.details': 'Détails',
            'common.total': 'Total',
            'common.showing': 'Affichage de',
            'common.to': 'à',
            'common.of': 'sur',
            'common.results': 'résultats',
            'common.generalInfo': 'Informations générales',
            'common.selectType': 'Sélectionner un type',
            'common.selectLevel': 'Sélectionner un niveau',
            'common.level': 'Niveau',
            'common.totalCost': 'Coût total',
            'common.projectLimit': 'Limite de projets',
            'common.description': 'Description',
            'common.saving': 'Enregistrement...',
            'common.planCreatedMessage': 'Le plan "{{plan}}" a été créé.',
            'common.planUpdatedMessage': 'Le plan "{{plan}}" a été mis à jour.',
            'notifications.title': 'Notifications',
            'notifications.newUser': 'Nouvel utilisateur inscrit',
            'notifications.newUserMessage': '{{name}} vient de s\'inscrire',
            'notifications.noNotifications': 'Aucune notification',
            'notifications.markAllRead': 'Tout marquer comme lu',
            'notifications.notificationDetails': 'Détails de la notification',
            'notifications.descriptionLabel': 'Description',
            'notifications.titleLabel': 'Titre',
            'notifications.allCaughtUp': 'Vous êtes à jour !',
            'dashboard.title': 'Tableau de bord',
            'dashboard.overview': 'Vue d\'ensemble des chantiers',
            'status.inProgress': 'En cours',
            'status.delayed': 'En retard',
            'status.pending': 'En attente',
            'status.completed': 'Terminé',
            'users.title': 'Gestion des utilisateurs',
            'help.title': 'Aide & Support',
            'help.description': 'Contactez-nous pour toute question ou assistance. Nous sommes là pour vous aider !',
            'help.email': 'Email',
            'help.phone': 'Téléphone',
            'help.hours': 'Heures de support :',
            'help.hoursValue': 'Lundi - Vendredi, 08h00 - 18h00',

            // Team Roles
            'team.roles.all': 'Tous les rôles',
            'team.roles.worker': 'Salarié',
            'team.roles.siteManager': 'Chef de chantier',
            'team.roles.subcontractor': 'Sous-traitant',
            'team.roles.promoter': 'Promoteur',
            'team.roles.masterBuilder': 'Maître d\'œuvre',
            'team.roles.projectOwner': 'Maître d\'ouvrage',
            'team.roles.architect': 'Architecte',
            'team.roles.engineer': 'Ingénieur',

            // Compte
            'common.completed': 'Terminé',
            'compte.title': 'Mon compte',
            'compte.tabs.info': 'Informations personnelles',
            'compte.tabs.subscriptions': 'Abonnements',
            'compte.tabs.invoices': 'Factures',
            'compte.form.firstName': 'Prénom',
            'compte.form.lastName': 'Nom',
            'compte.form.phone': 'Numéro de téléphone',
            'compte.form.email': 'Email',
            'compte.form.address': 'Adresse',
            'compte.form.company': 'Nom de l\'entreprise',
            'compte.form.profile': 'Profil',
            'compte.form.profileReadOnly': 'Le profil est en lecture seule',
            'compte.profile.cantEdit': 'Le profil ne peut pas être modifié',
            'compte.photo.newDetails': 'Nouvelle photo',
            'compte.photo.reminder': 'N\'oubliez pas de cliquer sur "Mettre à jour" pour enregistrer votre nouvelle photo.',
            'compte.subscription.current': 'Mon abonnement',
            'compte.subscription.expire': 'Date d\'expiration',
            'compte.subscription.viewOffers': 'Voir mes offres',
            'compte.subscription.detail': 'Détail de l\'abonnement',
            'compte.subscription.planName': 'Nom du plan',
            'compte.subscription.subtitle': 'Sous-titre',
            'compte.subscription.paymentStatus': 'Statut de paiement',
            'compte.subscription.paid': 'Payé',
            'compte.subscription.period': 'Période',
            'compte.subscription.amountPaid': 'Montant payé',
            'compte.subscription.paymentMethod': 'Moyen de paiement',
            'compte.subscription.benefits': 'Avantages inclus',
            'compte.subscription.monthly': 'Mensuel',
            'compte.subscription.yearly': 'Annuel',
            'compte.subscription.subscribe': 'S\'abonner',
            'compte.subscription.upgrade': 'Passer au Premium',
            'compte.subscription.includedBenefits': 'Avantages inclus',
            'compte.subscription.maxProjects': 'Projets maximum',
            'compte.subscription.prioritySupport': 'Support prioritaire',
            'compte.subscription.unlimitedProjects': 'Projets illimités',
            'compte.subscription.save': 'Économisez {{percent}}%',
            'compte.subscription.perMonth': 'mois',
            'compte.subscription.perMonthYearly': 'mois (facturé annuellement)',
            'compte.subscription.processing': 'Traitement en cours...',
            'compte.invoices.searchPlaceholder': 'Rechercher une facture...',
            'compte.invoices.search': 'Rechercher ici',
            'compte.invoices.allStatus': 'Tous les statuts',
            'compte.invoices.active': 'Actif',
            'compte.invoices.expired': 'Expiré',
            'compte.invoices.reference': 'Référence',
            'compte.invoices.amount': 'Montant',
            'compte.invoices.plan': 'Plan',
            'compte.invoices.status': 'Statut',
            'compte.invoices.pending': 'En attente',
            'compte.invoices.payNow': 'Payer maintenant',
            'compte.invoices.noInvoices': 'Aucune facture disponible',
            'compte.invoices.table.description': 'Description',
            'compte.invoices.table.period': 'Période',
            'compte.invoices.table.amountHT': 'Montant HT',
            'compte.save.loading': 'Enregistrement...',

            // Dashboard
            'dashboard.loading.title': 'Chargement du tableau de bord',
            'dashboard.loading.subtitle': 'Analyse des données en cours, veuillez patienter…',
            'dashboard.error.title': 'Impossible de charger les données',
            'dashboard.retry': 'Réessayer',
            'dashboard.searchProject': 'Rechercher par projet',
            'dashboard.overview.sites': 'Vue d\'ensemble des chantiers',
            'dashboard.overview.studies': 'Vue d\'ensemble des études',
            'dashboard.studies.distribution': 'Répartition des études (%)',
            'dashboard.studies.completed': 'Complétées',
            'dashboard.studies.initiated': 'En progression',
            'dashboard.studies.notInitiated': 'En attente',
            'dashboard.indicators.progress': 'Taux moyen d\'avancement',
            'dashboard.indicators.budget': 'Budget global',
            'dashboard.budget.consumed': 'Consommé',
            'dashboard.budget.remaining': 'Restant',
            'dashboard.alerts.stock': 'Alertes stock matériaux',
            'dashboard.alerts.none': 'Aucune alerte',
            'dashboard.charts.progress': 'État d\'avancement',
            'dashboard.charts.incidents': 'Incidents signalés (7 jours)',
            'dashboard.tasks.critical': 'Tâches critiques à échéance',
            'dashboard.photos.recent': 'Photos récentes',
            'dashboard.performance.summary': 'Résumé des performances',
            'dashboard.performance.avgPresence': 'Présence moyenne',
            'dashboard.performance.lateTasks': 'Tâches en retard',
            'dashboard.performance.alertMaterials': 'Matériaux en alerte',
            'dashboard.rate.avg': 'Taux moyen',
            'dashboard.metrics.inProgress': 'En cours',
            'dashboard.metrics.delayed': 'En retard',
            'dashboard.metrics.pending': 'En attente',
            'dashboard.metrics.completed': 'Terminées',
            'dashboard.metrics.total': 'En ensemble',
            'dashboard.metrics.delivered': 'Livrées',
            'dashboard.metrics.budgetConsumed': 'Budget consommé',
            'dashboard.metrics.incidents7d': 'Incidents (7j)',
            'demande.details.addReport': 'Ajouter un rapport',
            // Common / Other
            'common.month.yearly': 'mois (annuel)',
            'common.month': 'mois',
            'common.year': 'an',
            'common.updateSave': 'Mettre à jour',
            'common.processing': 'Traitement...',
            'common.economize': 'Économisez',
            'common.unlimitedProjects': 'Projets illimités',
            'common.maxProjects': 'projet(s) maximum',
            'common.prioritySupport': 'Support prioritaire',
            'common.installments': 'Paiement en {{count}} fois',
            'common.yearlyDiscount': 'réduction en paiement annuel',
            'common.popular': 'POPULAIRE',
            'common.changePhoto': 'Changer la photo',
            'common.print': 'Imprimer',

            // Generic Section Headers
            'requests.title': 'Demandes',
            'orders.title': 'Commandes',
            'subscriptions.title': 'Abonnements',
            'reclamations.title': 'Réclamations',

            // Réclamations
            'reclamations.pageTitle': 'Gestion des Réclamations',
            'reclamations.subtitle': 'Créer, modifier ou désactiver les plans d\'abonnement proposés aux utilisateurs',
            'reclamations.searchPlaceholder': 'Rechercher par utilisateur, sujet...',
            'reclamations.filters': 'Filtres',
            'reclamations.export': 'Exporter',
            'reclamations.table.user': 'Utilisateur',
            'reclamations.table.subject': 'Sujet',
            'reclamations.table.category': 'Catégorie',
            'reclamations.table.status': 'Statut',
            'reclamations.table.date': 'Date',
            'reclamations.table.actions': 'Actions',
            'reclamations.status.inProgress': 'En cours',
            'reclamations.status.resolved': 'Résolue',
            'reclamations.status.pending': 'En attente',
            'reclamations.pagination.showing': 'Affichage de',
            'reclamations.pagination.to': 'à',
            'reclamations.pagination.on': 'sur',
            'reclamations.pagination.results': 'résultats',
            'reclamations.pagination.next': 'Suivant',

            // Users Page
            'users.subtitle': 'Créer, modifier, visualiser, rechercher, filtrer les utilisateurs inscrits',
            'users.table.user': 'Utilisateur',
            'users.table.registerDate': 'Date d\'inscription',
            'users.create': 'Nouvel Utilisateur',
            'users.deleteConfirm': 'Êtes-vous sûr de vouloir supprimer cet utilisateur ?',

            // Export
            'export.select': 'Format d\'exportation',
            'export.excel': 'Exporter',
            'export.pdf': 'Exporter en PDF',
            'export.success': 'Exportation réussie',
            'export.error': 'Erreur lors de l\'exportation',

            // Projects
            'projects.title': 'Projets en cours',
            'projects.description': 'Consultez la liste de vos chantiers actuellement en cours et suivez leur avancement en temps réel.',
            'projects.newProject': 'Nouveau Projet',
            'projects.searchByProject': 'Rechercher par projet',
            'projects.allPeriods': 'Toutes les périodes',
            'projects.thisYear': 'Cette année',
            'projects.lastYear': 'Année dernière',
            'projects.older': 'Plus ancien',
            'projects.allStatuses': 'Tous les statuts',
            'projects.active': 'Actif',
            'projects.inactive': 'Inactif',
            'projects.completed': 'Terminé',
            'projects.inProgress': 'En cours',
            'projects.progress': 'Progression',
            'projects.loadingProjects': 'Chargement des projets...',
            'projects.noProjectFound': 'Aucun projet trouvé',
            'projects.retry': 'Réessayer',
            'projects.perPage': 'Projets par page :',
            'projects.page': 'Page',
            'projects.on': 'sur',
            'projects.noImage': 'Aucune image',
            'projects.blocked': 'BLOQUÉ',
            'projects.blockedTitle': 'Projet Bloqué',
            'projects.blockedRestricted': 'Accès restreint',
            'projects.blockedMessage': 'Ce projet est bloqué jusqu\'au renouvellement de votre abonnement. Veuillez mettre à jour votre plan pour y accéder.',
            'projects.subscriptions': 'Abonnements',
            'projects.showing': 'Affichage de',
            'projects.export': 'Exporter',

            // Project Detail Tabs
            'projectDetail.presentation': 'Présentation',
            'projectDetail.tasks': 'Tâches',
            'projectDetail.team': 'Équipe',
            'projectDetail.lots': 'Lots',
            'projectDetail.documents': 'Documents',
            'projectDetail.stock': 'Stock',
            'projectDetail.alert': 'Signalement',
            'projectDetail.studyBet': 'Etude Bet',
            'projectDetail.pointingAddress': 'Adresse Pointage',
            'projectDetail.viewQrCode': 'Voir QR Code',
            'projectDetail.qrCode': 'QR code',
            'projectDetail.scanMessage': 'Scannez avec votre téléphone pour ouvrir les détails du projet.',
            'projectDetail.project': 'Projet',
            'projectDetail.status': 'Statut',
            'projectDetail.startDate': 'Date de début',
            'projectDetail.endDate': 'Date de fin',
            'projectDetail.updateDate': 'Date MAJ',
            'projectDetail.address': 'Adresse',
            'projectDetail.print': 'Imprimer',
            'projectDetail.download': 'Télécharger',
            'projectDetail.qrUnavailable': 'QR code indisponible',
            'projectDetail.loading': 'Chargement...',
            'projectDetail.selectTab': 'Sélectionnez un onglet',
            'projectDetail.attendanceReport': 'Rapport de présence',

            // Project Detail - Statuses
            'projectStatus.active': 'Actif',
            'projectStatus.inactive': 'Inactif',
            'projectStatus.completed': 'Terminé',
            'projectStatus.inProgress': 'En cours',
            'projectStatus.pending': 'En attente',
            'projectStatus.blocked': 'Bloqué',

            // Project Detail - Empty Messages
            'projectDetail.noData': 'Aucune donnée disponible',
            'projectDetail.noTasks': 'Aucune tâche',
            'projectDetail.noTeam': 'Aucun membre d\'équipe',
            'projectDetail.noDocuments': 'Aucun document',
            'projectDetail.noStock': 'Aucun stock',
            'projectDetail.noAlerts': 'Aucune alerte',

            // Project Detail - Confirmations
            'projectDetail.deleteConfirm': 'Êtes-vous sûr de vouloir supprimer ?',
            'projectDetail.saveSuccess': 'Enregistré avec succès',
            'projectDetail.saveError': 'Erreur lors de l\'enregistrement',

            // Project Detail - Buttons
            'projectDetail.addTask': 'Ajouter une tâche',
            'projectDetail.addMember': 'Ajouter un membre',
            'projectDetail.uploadDocument': 'Téléverser un document',
            'projectDetail.addStock': 'Ajouter au stock',
            'projectDetail.reportIssue': 'Signaler un problème',

            // Dashboard Admin
            'dashboardAdmin.title': 'Tableau de bord Administrateur',
            'dashboardAdmin.users.total': 'Total utilisateurs',
            'dashboardAdmin.users.active': 'Utilisateurs actifs',
            'dashboardAdmin.users.pending': 'En attente de validation',
            'dashboardAdmin.projects.total': 'Total projets',
            'dashboardAdmin.subscriptions.total': 'Total Abonnements',

            'dashboardAdmin.subscriptions.active': 'Abonnements Actifs',
            'dashboardAdmin.subscriptions.expired': 'Abonnements Expirés',
            'dashboardAdmin.revenue.total': 'Revenus Cumulés',
            'dashboardAdmin.revenue.monthly': 'Revenus Mensuels',
            'dashboardAdmin.stats': 'Statistiques',
            'dashboardAdmin.overview': 'Vue d\'ensemble',
            'dashboardAdmin.charts.subscriptions': 'Évolution des Abonnements',
            'dashboardAdmin.charts.revenue': 'Revenus Mensuels',
            'dashboardAdmin.charts.profiles': 'Répartition des profils',
            'dashboardAdmin.charts.plans': 'Distribution des Plans',
            'dashboardAdmin.invoices.title': 'Dernières Factures Payées',
            'dashboardAdmin.invoices.invoice': 'Facture',
            'dashboardAdmin.invoices.user': 'Utilisateur',
            'dashboardAdmin.invoices.plan': 'Plan',
            'dashboardAdmin.invoices.amount': 'Montant',
            'dashboardAdmin.invoices.date': 'Date',
            'dashboardAdmin.invoices.status': 'Statut',
            'dashboardAdmin.invoices.paid': 'Payé',
            'dashboardAdmin.invoices.unpaid': 'Impayé',
            'dashboardAdmin.invoices.noData': 'Aucune facture disponible',
            'dashboardAdmin.pagination.showing': 'Affichage de',
            'dashboardAdmin.pagination.to': 'à',
            'dashboardAdmin.pagination.of': 'sur',
            'dashboardAdmin.pagination.results': 'résultats',
            'dashboardAdmin.pagination.previous': 'Précédent',
            'dashboardAdmin.pagination.next': 'Suivant',

            // Project Presentation
            'projectPresentation.progression': 'Progression',
            'projectPresentation.loadingProgress': 'Chargement de la progression...',
            'projectPresentation.surface': 'Surface',
            'projectPresentation.lots': 'Lots',
            'projectPresentation.startDate': 'Début',
            'projectPresentation.endDate': 'Fin prévue',
            'projectPresentation.budget': 'Budget',
            'projectPresentation.used': 'Utilisé',
            'projectPresentation.status': 'Statut',
            'projectPresentation.updating': 'Mise à jour...',
            'projectPresentation.inProgress': 'En cours',
            'projectPresentation.paused': 'En pause',
            'projectPresentation.wasteDisposal': 'Évacuation des déchets',
            'projectPresentation.equipment': 'Équipement',
            'projectPresentation.security': 'Sécurité',
            'projectPresentation.waterSupply': 'Alimentation en eau',
            // 'equipment.wasteDisposal':
            'projectPresentation.completed': 'Terminé',
            'projectPresentation.currentStatus': 'Statut actuel',
            'projectPresentation.statusChanged': 'Statut mis à jour',
            'projectPresentation.noSubcontractor': 'Aucun sous-traitant assigné',
            'projectPresentation.generalInfo': 'Informations générales',
            'projectPresentation.progressState': 'État d\'avancement',
            'projectPresentation.budgetTab': 'Budget',
            'projectPresentation.add': 'Ajouter',
            'projectPresentation.description': 'Description',
            'projectPresentation.amount': 'Montant',
            'projectPresentation.location': 'Emplacement',
            'projectPresentation.numberOfLots': 'Nombre de lots',
            'projectPresentation.dueDate': 'Date d\'échéance',
            'projectPresentation.numberOfRooms': 'Nombre de pièces',
            'projectPresentation.commonEquipment': 'Équipements communs',
            'projectPresentation.budgetInfo': 'Informations budgétaires',

            // ============ TEAM COMPONENT ============
            'team.searchPlaceholder': 'Rechercher ici...',
            'team.filterByRole': 'Filtrer par rôle',
            'team.allRoles': 'Tous les rôles',
            'team.newMember': 'Nouveau membre',
            'team.name': 'Nom',
            'team.role': 'Rôle',
            'team.phone': 'Téléphone',
            'team.present': 'Présent',
            'team.action': 'Action',
            'team.loading': 'Chargement en cours...',
            'team.retry': 'Réessayer',
            'team.yes': 'Oui',
            'team.no': 'Non',
            'team.noMembers': 'Aucun membre d\'équipe trouvé',
            'team.startAddingMembers': 'Commencez par ajouter des membres à votre équipe',
            'team.previous': 'Précédent',
            'team.next': 'Suivant',
            'team.addNewMember': 'Ajouter un nouveau membre',
            'team.lastName': 'Nom',
            'team.firstName': 'Prénom',
            'team.email': 'Email',
            'team.birthDate': 'Date de naissance',
            'team.birthPlace': 'Lieu de naissance',
            'team.address': 'Adresse',
            'team.profilePhoto': 'Photo de profil',
            'team.password': 'Mot de passe',
            'team.cancel': 'Annuler',
            'team.add': 'Ajouter',
            'team.adding': 'Ajout...',
            'team.details': 'Détails',
            'team.daysPresent': 'Jours présents',
            'team.hoursWorked': 'Heures travaillées',
            'team.tasksCompleted': 'Tâches terminées',
            'team.performance': 'Performance',
            'team.mon': 'Lun',
            'team.tue': 'Mar',
            'team.wed': 'Mer',
            'team.thu': 'Jeu',
            'team.fri': 'Ven',
            'team.sat': 'Sam',
            'team.sun': 'Dim',
            'team.taskDistribution': 'Répartition des tâches',
            'team.tasksTodo': 'À Faire',
            'team.tasksInProgress': 'En Cours',
            'team.tasksBlocked': 'Bloqué',
            'team.tasksDone': 'Terminé',
            'team.tasksLate': 'En Retard',

            // ============ TASK BOARD / TÂCHES ============
            'tasks.todo': 'À faire',
            'tasks.inProgress': 'En cours',
            'tasks.blocked': 'Bloqué',
            'tasks.done': 'Terminé',
            'team.presenceHistory': 'Historique des présences',
            'team.filterByDate': 'Filtrer par date',
            'team.entry': 'Entrée',
            'team.exit': 'Sortie',
            'team.total': 'Total',
            'team.noPresenceHistory': 'Aucun historique de présence disponible',
            'team.totalHoursWorked': 'Total Heures travaillées',
            'team.close': 'Fermer',
            'team.worker': 'Salarié',
            'team.acceptedFormats': 'Formats acceptés : JPG, PNG, GIF (Max 5MB)',
            'team.passwordMinLength': 'Mot de passe (minimum 6 caractères)',
            'team.profile': 'Profil',
            'team.profiles.worker': 'Salarié',
            'team.profiles.siteManager': 'Chef de chantier',
            'team.profiles.subcontractor': 'Sous-traitant',
            'team.profiles.promoteur': 'Promoteur',
            'team.editMember': 'Modifier le salarié',
            'team.save': 'Enregistrer',
            'team.saving': 'Enregistrement...',
            'team.loadingData': 'Chargement des informations...',
            'team.phoneReadOnly': 'Identifiant de connexion — non modifiable',
            'team.deleteTitle': 'Supprimer le salarié ?',
            'team.deleteConfirmBefore': 'Voulez-vous vraiment supprimer',
            'team.deleteConfirmAfter': '? Cette action est irréversible.',
            'team.delete': 'Supprimer',
            'team.deleting': 'Suppression...',
            'team.deleteSuccess': 'Salarié supprimé',
            'team.deleteSuccessMsg': 'a bien été supprimé de votre équipe.',
            'team.deleteError': 'Erreur lors de la suppression. Veuillez réessayer.',
            'team.deleteConstraintError': 'Ce salarié ne peut pas être supprimé car il possède des données liées (congés, pointages…).',
            'team.deactivateInstead': 'Désactiver le compte à la place',
            'team.deactivating': 'Désactivation...',
            'team.deactivateSuccess': 'Compte désactivé',
            'team.deactivateSuccessMsg': 'a été désactivé et n\'aura plus accès à l\'application.',

            // ============ DOCUMENTS COMPONENT ============
            'documents.title': 'Documents',
            'documents.search': 'Rechercher un document...',
            'documents.allTypes': 'Tous les types',
            'documents.addDocument': 'Ajouter un document',
            'documents.name': 'Nom',
            'documents.type': 'Type',
            'documents.size': 'Taille',
            'documents.date': 'Date',
            'documents.actions': 'Actions',
            'documents.download': 'Télécharger',
            'documents.delete': 'Supprimer',
            'documents.view': 'Voir',
            'documents.noDocuments': 'Aucun document trouvé',
            'documents.uploadDocument': 'Téléverser un document',
            'documents.dragDrop': 'Glissez et déposez vos fichiers ici',
            'documents.or': 'ou',
            'documents.browse': 'Parcourir',
            'documents.cancel': 'Annuler',
            'documents.upload': 'Téléverser',
            'documents.uploading': 'Téléversement...',
            'documents.deleteConfirm': 'Êtes-vous sûr de vouloir supprimer ce document ?',
            'documents.confirmDelete': 'Confirmer la suppression',
            'documents.category': 'Catégorie',
            'documents.allCategories': 'Toutes les catégories',
            'documents.administrative': 'Administratif',
            'documents.technical': 'Technique',
            'documents.financial': 'Financier',
            'documents.other': 'Autre',

            // ============ STOCK COMPONENT ============
            'stock.title': 'Stock',
            'stock.search': 'Rechercher un article...',
            'stock.addArticle': 'Ajouter un article',
            'stock.articleName': 'Nom de l\'article',
            'stock.category': 'Catégorie',
            'stock.quantity': 'Quantité',
            'stock.movements.entry': 'Entrée',
            'stock.movements.exit': 'Sortie',
            'stock.movements.save': 'Enregistrer',
            'stock.movements.cancel': 'Annuler',
            'stock.movements': 'Mouvements',
            'stock.movements.title': 'Titre',

            'stock.unit': 'Unité',
            // 'stock.alerts.title': 'Alertes',
            'stock.minStock': 'Stock minimum',
            'stock.maxStock': 'Stock maximum',
            'stock.reorderPoint': 'Point de commande',
            'stock.currentStock': 'Stock actuel',
            'stock.status': 'Statut',
            'stock.actions': 'Actions',
            'stock.inStock': 'En stock',
            'stock.lowStock': 'Stock bas',
            'stock.outOfStock': 'Rupture de stock',
            'stock.edit': 'Modifier',
            'stock.delete': 'Supprimer',
            'stock.noArticles': 'Aucun article en stock',
            'stock.addNewArticle': 'Ajouter un nouvel article',
            'stock.editArticle': 'Modifier l\'article',
            'stock.cancel': 'Annuler',
            'stock.save': 'Enregistrer',
            'stock.saving': 'Enregistrement...',
            'stock.deleteConfirm': 'Êtes-vous sûr de vouloir supprimer cet article ?',
            'stock.confirmDelete': 'Confirmer la suppression',
            'stock.allCategories': 'Toutes les catégories',
            'stock.materials': 'Matériaux',
            'stock.tools': 'Outils',
            'stock.equipment': 'Équipements',
            'stock.consumables': 'Consommables',
            'stock.supplier': 'Fournisseur',
            'stock.tabs.inventories': 'Inventaires',
            'stock.tabs.orders': 'Commandes',
            'stock.tabs.deliveries': 'Livraisons',
            'stock.tabs.statistics': 'Statistiques',
            'stock.newMaterial': 'Nouveau Matériau',
            'stock.newOrder': 'Nouvelle Commande',
            'stock.reset': 'Réinitialiser',
            'stock.filters.allMaterials': 'Tous les matériaux',
            'stock.filters.allTools': 'Tous les outils',
            'stock.filters.allEquipment': 'Tous les équipements',
            'stock.filters.allConsumables': 'Tous les consommables',
            'stock.filters.searchMaterial': 'Rechercher un matériau...',
            'stock.filters.searchTool': 'Rechercher un outil...',
            'stock.filters.searchEquipment': 'Rechercher un équipement...',
            'stock.filters.searchConsumable': 'Rechercher un consommable...',
            'stock.filters.searchSupplier': 'Rechercher un fournisseur...',
            'stock.table.threshold': 'Seuil',
            'stock.table.state': 'État',
            'stock.table.unitPrice': 'Prix unitaire',
            'stock.table.totalValue': 'Valeur totale',
            'stock.table.lastUpdated': 'Dernière mise à jour',
            'stock.table.addStock': 'Ajouter du stock',
            'stock.table.removeStock': 'Retirer du stock',
            'stock.movement': 'Mouvement',
            'stock.movementHistory': 'Historique des mouvements',
            'stock.orderDate': 'Date de commande',
            'stock.phone': 'Téléphone',
            'stock.quotes': 'Citations',
            'stock.quote': 'Citation',
            'stock.selected': 'Sélectionnée',
            'stock.supplierName': 'Fournisseur',
            'stock.uploadDate': 'Date d\'envoi',
            'stock.noQuotes': 'Aucune citation disponible',
            'stock.noQuotesDesc': 'Les fournisseurs n\'ont pas encore envoyé de citations',
            'stock.orderedMaterials': 'Matériaux commandés',
            'stock.approveOrder': 'Approuver la commande',
            'stock.approveOrderTitle': 'Approuver cette commande ?',
            'stock.approveOrderMessage': 'Vous êtes sur le point d\'approuver la commande',
            'stock.totalAmount': 'Montant total',
            'stock.approveOrderWarning': 'Cette action validera la citation et permettra le traitement de la commande.',
            'stock.approving': 'Approbation...',
            'stock.confirmApproval': 'Confirmer l\'approbation',
            'stock.downloadInvoice': 'Télécharger la facture',

            // Stock - Additional translations
            // 'stock.filters.searchPlaceholder': 'Rechercher par matériel, unité...',
            'stock.filters.normalStock': 'Stock normal',
            'stock.filters.lowStock': 'Stock faible',
            'stock.filters.outOfStock': 'Rupture de stock',
            'stock.loading.materials': 'Chargement des matériaux…',
            'stock.pagination.showing': 'Affichage de',
            'stock.pagination.materials': 'matériaux',
            'stock.pagination.orders': 'commandes',
            'stock.pagination.deliveries': 'livraisons',
            'stock.actions.manageMovements': 'Gérer les mouvements',
            'stock.actions.history': 'Historique',
            'stock.actions.delete': 'Supprimer',
            'stock.actions.viewDetails': 'Voir détails',
            'stock.actions.modify': 'Modifier',
            'stock.orders.searchPlaceholder': 'Rechercher par commande, fournisseur',
            'stock.orders.allStatuses': 'Tous les statuts',
            'stock.orders.orderNumber': 'N° Commande',
            'stock.orders.supplier': 'Fournisseur',
            'stock.deliveries.searchPlaceholder': 'Rechercher par livraison, fournisseur',
            'stock.deliveries.allDeliveries': 'Toutes les livraisons',
            'stock.deliveries.deliveryNumber': 'N° Livraison',
            'stock.deliveries.proof': 'Preuve',
            'stock.deliveries.noProof': 'Aucune',
            'stock.deliveries.downloadProof': 'Télécharger preuve',
            'stock.status.pending': 'En attente',
            'stock.status.inDelivery': 'En livraison',
            'stock.status.delivered': 'Livrée',
            'stock.status.cancelled': 'Annulée',
            'stock.status.approved': 'Approuvée',
            'stock.status.complete': 'Complète',
            'stock.status.partial': 'Partielle',

            'stock.alerts.critical': 'Critique',
            'stock.alerts.low': 'Faible',
            'stock.alerts.normal': 'Normal',
            'stock.table.material': 'Matériau',
            'stock.table.stock': 'Stock',
            'stock.table.unit': 'Unité',
            'stock.table.actions': 'Actions',
            'stock.table.date': 'Date',
            'stock.table.status': 'Statut',

            // Stock Statistics
            'stock.stats.materialsUsed': 'Matériaux utilisés',
            'stock.stats.ordersPlaced': 'Commandes passées',
            'stock.stats.deliveriesReceived': 'Livraisons reçues',
            'stock.stats.alertsTriggered': 'Alertes déclenchées',
            'stock.stats.categoryDistribution': 'Répartition par catégorie',
            'stock.stats.totalCategories': 'Total des catégories :',


            // ============ LOTS COMPONENT ============
            'lots.title': 'Lots',
            'lots.search': 'Rechercher un lot...',
            'lots.addLot': 'Ajouter un lot',
            'lots.lotNumber': 'Numéro de lot',
            'lots.lotName': 'Nom du lot',
            'lots.description': 'Description',
            'lots.subcontractor': 'Sous-traitant',
            'lots.budget': 'Budget',
            'lots.progress': 'Avancement',
            'lots.status': 'Statut',
            'lots.actions': 'Actions',
            'lots.notStarted': 'Non démarré',
            'lots.inProgress': 'En cours',
            'lots.completed': 'Terminé',
            'lots.suspended': 'Suspendu',
            'lots.noLots': 'Aucun lot trouvé',
            'lots.addNewLot': 'Ajouter un nouveau lot',
            'lots.editLot': 'Modifier le lot',
            'lots.cancel': 'Annuler',
            'lots.save': 'Enregistrer',
            'lots.saving': 'Enregistrement...',
            'lots.deleteConfirm': 'Êtes-vous sûr de vouloir supprimer ce lot ?',
            'lots.confirmDelete': 'Confirmer la suppression',
            'lots.assignSubcontractor': 'Assigner un sous-traitant',
            'lots.selectSubcontractor': 'Sélectionner un sous-traitant',
            'lots.noSubcontractor': 'Aucun sous-traitant assigné',
            'lots.startDate': 'Date de début',
            'lots.endDate': 'Date de fin',
            'lots.estimatedBudget': 'Budget estimé',
            'lots.actualBudget': 'Budget réel',
            'lots.view': 'Voir',
            'lots.edit': 'Modifier',
            'lots.delete': 'Supprimer',

            // ============ ALERT/SIGNALEMENT COMPONENT ============
            'alert.title': 'Signalements',
            'alert.search': 'Rechercher un signalement...',
            'alert.addAlert': 'Nouveau signalement',
            'alert.alertType': 'Type de signalement',
            'alert.priority': 'Priorité',
            'alert.status': 'Statut',
            'alert.date': 'Date',
            'alert.reportedBy': 'Signalé par',
            'alert.description': 'Description',
            'alert.actions': 'Actions',
            'alert.high': 'Haute',
            'alert.medium': 'Moyenne',
            'alert.low': 'Basse',
            'alert.open': 'Ouvert',
            'alert.inProgress': 'En cours',
            'alert.resolved': 'Résolu',
            'alert.closed': 'Fermé',
            'alert.noAlerts': 'Aucun signalement',
            'alert.addNewAlert': 'Ajouter un signalement',
            'alert.editAlert': 'Modifier le signalement',
            'alert.cancel': 'Annuler',
            'alert.save': 'Enregistrer',
            'alert.saving': 'Enregistrement...',
            'alert.deleteConfirm': 'Êtes-vous sûr de vouloir supprimer ce signalement ?',
            'alert.confirmDelete': 'Confirmer la suppression',
            'alert.safety': 'Sécurité',
            'alert.quality': 'Qualité',
            'alert.delay': 'Retard',
            'alert.material': 'Matériel',
            'alert.other': 'Autre',
            'alert.location': 'Localisation',
            'alert.photos': 'Photos',
            'alert.addPhotos': 'Ajouter des photos',
            'alert.view': 'Voir',
            'alert.edit': 'Modifier',
            'alert.delete': 'Supprimer',
            'alert.resolve': 'Résoudre',
            'alert.reopen': 'Rouvrir',
            'alert.allTypes': 'Tous les types',
            'alert.allPriorities': 'Toutes les priorités',
            'alert.allStatuses': 'Tous les statuts',

            // ============ STUDY BET COMPONENT ============
            'studyBet.title': 'Études BET',
            'studyBet.search': 'Rechercher une étude...',
            'studyBet.addStudy': 'Nouvelle étude',
            'studyBet.studyName': 'Nom de l\'étude',
            'studyBet.studyType': 'Type d\'étude',
            'studyBet.office': 'Bureau d\'études',
            'studyBet.status': 'Statut',
            'studyBet.deliveryDate': 'Date de livraison',
            'studyBet.actions': 'Actions',
            'studyBet.info': 'Information',
            'studyBet.details': 'Détails',
            'studyBet.description': 'Description',
            'studyBet.amount': 'Montant',

            'studyBet.location': 'Localisation',
            'studyBet.numberOfLots': 'Nombre de lots',
            'studyBet.dueDate': 'Date d\'échéance',
            'studyBet.numberOfRooms': 'Nombre de chambres',
            'studyBet.commonEquipment': 'Équipement commun',
            'studyBet.budgetInfo': 'Informations sur le budget',
            'studyBet.titleLabel': 'Titre',
            'studyBet.creationDate': 'Date de création',
            'studyBet.concernedBet': 'BET concerné',
            'studyBet.associatedReports': 'Rapports associés',
            'studyBet.submittedOn': 'Soumis le',
            'studyBet.close': 'Fermer',
            'studyBet.listTitle': 'Liste des études BET',
            'studyBet.betName': 'Nom du BET',
            'studyBet.noReports': 'Aucun rapport trouvé',
            'studyBet.loading': 'Chargement...',
            'studyBet.state': 'État',
            'studyBet.step1': 'Étape 1',
            'studyBet.step2': 'Étape 2',
            'studyBet.step3': 'Étape 3',
            'studyBet.step4': 'Étape 4',
            'studyBet.pending': 'En attente',
            'studyBet.inProgress': 'En cours',
            'studyBet.delivered': 'Livré',
            'studyBet.validated': 'Validé',
            'studyBet.rejected': 'Rejeté',
            'studyBet.noStudies': 'Aucune étude trouvée',
            'studyBet.addNewStudy': 'Ajouter une étude',
            'studyBet.editStudy': 'Modifier l\'étude',
            'studyBet.table.title': 'Titre',
            'studyBet.table.description': 'Description',
            'studyBet.table.creationDate': 'Date de création',
            'studyBet.table.status': 'Statut',
            'studyBet.table.actions': 'Actions',
            'studyBet.cancel': 'Annuler',
            'studyBet.table.betName': 'Nom du BET',
            'studyBet.pagination.results': 'Résultats',
            'studyBet.pagination.of': 'de',
            'studyBet.pagination.to': 'à',
            'studyBet.pagination.showing': 'Affichage',
            'studyBet.pagination.previous': 'Précédent',
            'studyBet.pagination.next': 'Suivant',
            'studyBet.newStudy': 'Nouvelle étude',
            'studyBet.newStudy.title': 'Nouvelle étude',
            'studyBet.newStudy.description': 'Description',
            'studyBet.newStudy.creationDate': 'Date de création',
            'studyBet.newStudy.status': 'Statut',
            'studyBet.newStudy.actions': 'Actions',
            'studyBet.newStudy.cancel': 'Annuler',
            'studyBet.newStudy.table.betName': 'Nom du BET',
            'studyBet.newStudy.pagination.results': 'Résultats',
            'studyBet.newStudy.pagination.of': 'de',
            'studyBet.newStudy.pagination.to': 'à',
            'studyBet.newStudy.pagination.showing': 'Affichage',
            'studyBet.newStudy.pagination.previous': 'Précédent',
            'studyBet.newStudy.pagination.next': 'Suivant',
            'studyBet.save': 'Enregistrer',
            'studyBet.saving': 'Enregistrement...',
            'studyBet.deleteConfirm': 'Êtes-vous sûr de vouloir supprimer cette étude ?',
            'studyBet.confirmDelete': 'Confirmer la suppression',
            'studyBet.structural': 'Structure',
            'studyBet.electrical': 'Électricité',
            'studyBet.plumbing': 'Plomberie',
            'studyBet.hvac': 'CVC',
            'studyBet.architectural': 'Architecture',
            'studyBet.geotechnical': 'Géotechnique',
            'studyBet.other': 'Autre',
            'studyBet.documents': 'Documents',
            'studyBet.comments': 'Commentaires',
            'studyBet.version': 'Version',
            'studyBet.view': 'Voir',
            'studyBet.edit': 'Modifier',
            'studyBet.delete': 'Supprimer',
            'studyBet.validate': 'Valider',
            'studyBet.reject': 'Rejeter',
            'studyBet.download': 'Télécharger',

            // ============ POINTING ADDRESS COMPONENT ============
            'pointingAddress.title': 'Adresse de pointage',
            'pointingAddress.search': 'Rechercher...',
            'pointingAddress.addAddress': 'Ajouter une adresse',
            'pointingAddress.addressName': 'Nom de l\'adresse',
            'pointingAddress.fullAddress': 'Adresse complète',
            'pointingAddress.latitude': 'Latitude',
            'pointingAddress.longitude': 'Longitude',
            'pointingAddress.radius': 'Rayon (mètres)',
            'pointingAddress.status': 'Statut',
            'pointingAddress.actions': 'Actions',
            'pointingAddress.active': 'Actif',
            'pointingAddress.inactive': 'Inactif',
            'pointingAddress.noAddresses': 'Aucune adresse de pointage',
            'pointingAddress.addNewAddress': 'Ajouter une adresse de pointage',
            'pointingAddress.editAddress': 'Modifier l\'adresse',
            'pointingAddress.cancel': 'Annuler',
            'pointingAddress.save': 'Enregistrer',
            'pointingAddress.saving': 'Enregistrement...',
            'pointingAddress.deleteConfirm': 'Êtes-vous sûr de vouloir supprimer cette adresse ?',
            'pointingAddress.confirmDelete': 'Confirmer la suppression',
            'pointingAddress.selectOnMap': 'Sélectionner sur la carte',
            'pointingAddress.currentLocation': 'Position actuelle',
            'pointingAddress.view': 'Voir',
            'pointingAddress.edit': 'Modifier',
            'pointingAddress.delete': 'Supprimer',
            'pointingAddress.activate': 'Activer',
            'pointingAddress.deactivate': 'Désactiver',

            // ============ STATUS REPORT/ALBUMS COMPONENT ============
            'statusReport.albumsView': 'Vue Albums',
            'statusReport.graphView': 'Vue Graphique',
            'statusReport.indicatorsTable': 'Tableau indicateurs',
            'statusReport.viewPhotos': 'Voir les photos',
            'statusReport.editAlbum': 'Modifier l\'album',
            'statusReport.deleteAlbum': 'Supprimer l\'album',
            'statusReport.photos': 'photos',
            'statusReport.step': 'Étape',
            'statusReport.percentage': '%Pourcentage',
            'statusReport.updateDate': 'Date Mise À Jour',
            'statusReport.selectPercentage': 'Sélectionner une pourcentage',
            'statusReport.close': 'Fermer',
            'statusReport.createAlbum': 'Créer un nouvel album',
            'statusReport.albumName': 'Nom de l\'album',
            'statusReport.description': 'Description',
            'statusReport.albumPhotos': 'Photos de l\'album',
            'statusReport.clickToAdd': 'Cliquez pour ajouter des photos ou glissez-déposez',
            'statusReport.acceptedFormats': 'PNG, JPG, JPEG jusqu\'à 10MB',
            'statusReport.selectedPhotos': 'Photos sélectionnées',
            'statusReport.cancel': 'Annuler',
            'statusReport.create': 'Créer',
            'statusReport.modify': 'Modifier',
            'statusReport.currentPhotos': 'Photos actuelles',
            'statusReport.addNewPhotos': 'Ajouter de nouvelles photos',
            'statusReport.confirmDeletion': 'Confirmer la suppression',
            'statusReport.deleteConfirmMessage': 'Êtes-vous sûr de vouloir supprimer l\'album',
            'statusReport.irreversibleAction': 'Cette action est irréversible.',
            'statusReport.noCancel': 'Non, annuler',
            'statusReport.yesDelete': 'Oui, supprimer',
            'statusReport.noPhotos': 'Aucune photo dans cet album',

            // Logout Confirmation
            'logout.confirmTitle': 'Confirmer la déconnexion',
            'logout.confirmMessage': 'Êtes-vous sûr de vouloir vous déconnecter ?',
            'logout.confirm': 'Se déconnecter',

            // Equipements
            'equipment.hall': 'Hall d\'entrée',
            'equipment.hallDesc': 'Espace d\'accueil de l\'immeuble',
            'equipment.elevator': 'Ascenseur',
            'equipment.elevatorDesc': 'Accès aux différents niveaux',
            'equipment.parking': 'Parking',
            'equipment.parkingDesc': 'Espaces de stationnement',
            'equipment.pool': 'Piscine',
            'equipment.poolDesc': 'Bassin de natation',
            'equipment.gym': 'Salle de sport',
            'equipment.gymDesc': 'Espace fitness équipé',
            'equipment.playground': 'Terrain de jeux',
            'equipment.playgroundDesc': 'Espace de jeux pour enfants',
            'equipment.security': 'Service de sécurité',
            'equipment.securityDesc': 'Surveillance 24/7',
            'equipment.garden': 'Jardin',
            'equipment.gardenDesc': 'Espace vert paysager',
            'equipment.terrace': 'Terrasse partagée',
            'equipment.terraceDesc': 'Espace extérieur commun',
            'equipment.bikeStorage': 'Local à vélos',
            'equipment.bikeStorageDesc': 'Stationnement sécurisé pour vélos',
            'equipment.laundry': 'Laverie',
            'equipment.laundryDesc': 'Espace buanderie commun',
            'equipment.storage': 'Locaux de stockage',
            'equipment.storageDesc': 'Espaces de rangement individuels',
            'equipment.corridors': 'Couloirs',
            'equipment.mezzanine': 'Mezzanine',
            'allRoles': 'Tous les roles',
            'equipment.mezzanineDesc': 'Espaces de circulation',
            'equipment.wasteDisposal': 'Espaces de circulation',
            'equipment.wasteDisposalDesc': 'Espaces de circulation',
            'equipment.corridorsDesc': 'Espaces de circulation',

            // ============ PROJECT BUDGET COMPONENT ============
            'projectBudget.title': 'Gestion du Budget',
            'projectBudget.editBudget': 'Modifier Budget',
            'projectBudget.loading': 'Chargement en cours...',
            'projectBudget.plannedBudget': 'Budget prévu',
            'projectBudget.remainingBudget': 'Budget restant',
            'projectBudget.consumedBudget': 'Budget utilisé',
            'projectBudget.expensesList': 'Liste des dépenses',
            'projectBudget.newExpense': 'Nouvelle dépense',
            'projectBudget.date': 'Date',
            'projectBudget.description': 'Description',
            'projectBudget.evidence': 'Preuve',
            'projectBudget.amount': 'Montant',
            'projectBudget.actions': 'Actions',
            'projectBudget.noExpenses': 'Aucune dépense',
            'projectBudget.startAdding': 'Commencez par ajouter une nouvelle dépense.',
            'projectBudget.previous': 'Précédent',
            'projectBudget.next': 'Suivant',
            'projectBudget.editBudgetModal': 'Modifier le budget',
            'projectBudget.budgetAmount': 'Montant du budget',
            'projectBudget.cancel': 'Annuler',
            'projectBudget.save': 'Enregistrer',
            'projectBudget.saving': 'Enregistrement...',
            'projectBudget.editExpenseModal': 'Modifier la dépense',
            'projectBudget.newExpenseModal': 'Nouvelle dépense',
            'projectBudget.expenseDescription': 'Description de la dépense',
            'projectBudget.amountInCurrency': 'Montant',
            'projectBudget.evidenceOptional': 'Justificatif (optionnel)',
            'projectBudget.clickToBrowse': 'Cliquez pour parcourir ou glissez un fichier',
            'projectBudget.acceptedFormats': 'Formats acceptés : PDF, JPG, PNG • Taille max : 5MB',
            'projectBudget.confirmDelete': 'Confirmer la suppression',
            'projectBudget.deleteConfirmMessage': 'Êtes-vous sûr de vouloir supprimer cette dépense ?',
            'projectBudget.irreversibleAction': 'Cette action est irréversible.',
            'projectBudget.delete': 'Supprimer',
            'projectStatus.suspended': 'Suspendu',
            'projectStatus.cancelled': 'Annulé',
            'projectStatus.planning': 'Planning',
            'projectStatus.execution': 'Execution',

            'projectBudget.deleting': 'Suppression...',
            'projectBudget.evidenceModal': 'Preuve de la dépense',
            'projectBudget.noEvidence': 'Aucune preuve disponible',
            'projectBudget.downloadEvidence': 'Télécharger la preuve',
            'projectBudget.close': 'Fermer',
            'common.viewDetails': 'Voir détails',
            'projectBudget.fileTooBig': 'Le fichier ne doit pas dépasser 5MB',
            'projectBudget.invalidFormat': 'Format non supporté. Utilisez PDF, JPG ou PNG',

            // ============ TASK BOARD / TÂCHES (Complement) ============
            'taskBoard.title': 'Tableau des tâches',
            'taskBoard.searchPlaceholder': 'Rechercher ici...',
            'taskBoard.newTask': 'Nouvelle tâche',
            'taskBoard.editTask': 'Modifier la tâche',
            'taskBoard.column.TODO': 'À faire',
            'taskBoard.column.IN_PROGRESS': 'En cours',
            'taskBoard.column.BLOCKED': 'Bloqué',
            'taskBoard.column.DONE': 'Terminé',
            'taskBoard.task.comments': 'Commentaires',
            'taskBoard.task.attachments': 'Fichiers joints',
            'taskBoard.task.dueDate': 'Échéance',
            'taskBoard.task.noTasks': 'Aucune tâche',
            'taskBoard.task.clickToAdd': 'Cliquez sur + pour ajouter une tâche',
            'taskBoard.task.scrollMore': 'Scrollez pour voir plus',
            'taskBoard.form.title': 'Titre',
            'taskBoard.form.description': 'Description',
            'taskBoard.form.priority': 'Priorité',
            'taskBoard.form.startDate': 'Date de début',
            'taskBoard.form.endDate': 'Date de fin',
            'taskBoard.form.status': 'Statut',
            'taskBoard.form.assignUsers': 'Assigner à des salariés',
            'taskBoard.form.selectWorkers': 'Salariés sélectionnés',
            'taskBoard.form.noWorkerAvailable': 'Aucun salarié disponible',
            'taskBoard.form.clickToAssign': 'Cliquez sur un salarié pour l\'assigner',
            'taskBoard.form.images': 'Images',
            'taskBoard.form.clickToAddImages': 'Cliquez pour ajouter des images',
            'taskBoard.form.imageFormat': 'PNG, JPG jusqu\'à 10MB',
            'taskBoard.form.selectedFiles': 'fichier(s) sélectionné(s)',
            'taskBoard.modal.joinFileTitle': 'Joindre un fichier',
            'taskBoard.modal.fileLabel': 'Libellé du fichier',
            'taskBoard.modal.selectFile': 'Sélectionner un fichier',
            'taskBoard.modal.clickToSelect': 'Cliquez pour sélectionner',
            'taskBoard.modal.dragDrop': 'ou glissez-déposez un fichier',
            'taskBoard.modal.joinFileButton': 'Joindre le fichier',
            'taskBoard.detail.gallery': 'Galerie d\'images',
            'taskBoard.detail.viewBig': 'Voir en grand',
            'taskBoard.detail.period': 'Période',
            'taskBoard.detail.start': 'Début',
            'taskBoard.detail.end': 'Fin',
            'taskBoard.detail.stats': 'Statistiques',
            'taskBoard.detail.materialsUsed': 'Matériaux utilisés',
            'taskBoard.detail.assignedTeam': 'Équipe assignée',
            'taskBoard.detail.executor': 'Exécuteur',
            'taskBoard.detail.noExecutor': 'Aucun exécuteur assigné',
            'taskBoard.detail.clickEditToAssign': 'Cliquez sur "Modifier" pour assigner des membres',
            'taskBoard.priority.LOW': 'Basse',
            'taskBoard.priority.MEDIUM': 'Moyenne',
            'taskBoard.priority.HIGH': 'Haute',
            'taskBoard.modal.attachedFiles': 'Fichiers actuellement attachés',
            'taskBoard.modal.addNewFile': 'Ajouter un nouveau fichier',
            'taskBoard.modal.selectedFile': 'Fichier sélectionné',
            'taskBoard.modal.joining': 'Jointure en cours...',
            'taskBoard.task.members': 'membre(s)',

            // ============ LOTS (Complement) ============
            'lots.searchPlaceholder': 'Rechercher par nom de lot ou sous-traitant...',
            'lots.allStatus': 'Tous les statuts',
            'lots.newLot': 'Nouveau lot',
            'lots.progressPercentage': 'Pourcentage de progression',
            'lots.preview': 'Aperçu',
            'lots.commentsTitle': 'Commentaires du lot',
            'lots.commentsHistory': 'Historique des commentaires',
            'lots.noComments': 'Aucun commentaire',
            'lots.beFirstToComment': 'Soyez le premier à commenter ce lot',
            'lots.addComment': 'Ajouter un commentaire',
            'lots.yourComment': 'Votre commentaire',
            'lots.commentPlaceholder': 'Partagez vos observations, questions ou remarques...',
            'lots.detailsTitle': 'Détails du lot',
            'lots.assignedSubcontractor': 'Sous-traitant assigné',
            'lots.attachedDocuments': 'Documents joints',
            'lots.property': 'Propriété',
            'lots.subcontractorPlaceholder': 'Rechercher un sous-traitant...',
            'lots.noSubcontractorFound': 'Aucun sous-traitant trouvé',
            'lots.selectedSubcontractor': 'Sous-traitant sélectionné',
            'lots.change': 'Changer',
            'lots.updateProgress': 'Modifier la progression',
            'lots.validate': 'Valider',
            'lots.updating': 'Mise à jour...',
            'lots.sending': 'Envoi...',
            'lots.send': 'Envoyer',
            'lots.documentName': 'Nom du document',
            'Confirm Deletion': 'Confirmer la suppression',
            'Are you sure you want to delete this document?': 'Êtes-vous sûr de vouloir supprimer ce document ?',
            'Cancel': 'Annuler',
            'Delete': 'Supprimer',

            // Lots Status & Errors
            'lots.status.PENDING': 'En attente',
            'lots.status.IN_PROGRESS': 'En cours',
            'lots.status.COMPLETED': 'Terminé',
            'lots.error.missingPropertyId': 'ID de propriété manquant dans l\'URL',
            'lots.error.sessionExpired': 'Session expirée. Veuillez vous reconnecter.',
            'lots.error.fileTooBig': 'Le fichier est trop volumineux (max 10 Mo)',
            'lots.error.noFileSelected': 'Aucun fichier sélectionné',
            'lots.error.documentLabelRequired': 'Le libellé du document est requis',
            'lots.success.documentAdded': 'Document ajouté avec succès !',
            'lots.error.documentUploadFailed': 'Échec de l\'upload du document',
            'lots.confirmDeleteDocument': 'Êtes-vous sûr de vouloir supprimer ce document ?',
            'lots.success.documentDeleted': 'Document supprimé avec succès !',
            'lots.error.deleteDocumentFailed': 'Erreur lors de la suppression du document',
            'lots.error.loadSubcontractors': 'Impossible de charger les sous-traitants',
            'lots.error.loadLots': 'Impossible de charger les lots',
            'lots.error.percentageRange': 'Le pourcentage doit être entre 0 et 100',
            'lots.success.progressUpdated': 'Progression mise à jour avec succès !',
            'lots.error.progressUpdateFailed': 'Erreur lors de la mise à jour de la progression',
            'lots.success.statusChanged': 'Statut modifié avec succès !',
            'lots.error.statusChangeFailed': 'Erreur lors du changement de statut',
            'lots.error.lotNameRequired': 'Le nom du lot est requis',
            'lots.error.descriptionRequired': 'La description est requise',
            'lots.error.startDateRequired': 'La date de début est requise',
            'lots.error.endDateRequired': 'La date de fin est requise',
            'lots.error.dateRangeInvalid': 'La date de fin doit être après la date de début',
            'lots.error.subcontractorRequired': 'Veuillez sélectionner un sous-traitant',
            'lots.success.lotCreated': 'Lot créé avec succès !',
            'lots.success.lotUpdated': 'Lot modifié avec succès !',
            'lots.error.serverConnect': 'Erreur de connexion au serveur',
            'lots.error.invalidData': 'Données invalides',
            'lots.error.accessDenied': 'Vous n\'avez pas les droits pour effectuer cette action',
            'lots.error.notFound': 'Propriété ou sous-traitant introuvable',
            'lots.error.serverError': 'Erreur serveur. Veuillez réessayer plus tard',
            'lots.error.saveFailed': 'Une erreur est survenue lors de la sauvegarde',
            'lots.error.fileSizeLimit': 'Le fichier ne doit pas dépasser 5MB',
            'lots.error.fileFormat': 'Format non supporté. Utilisez PDF, JPG ou PNG',

            // ============ DOCUMENTS (Complement) ============
            'documents.allStatus': 'Tous les statuts',
            'documents.recentActivities': 'Activités récentes',
            'documents.lastModified': 'Dernière modification',
            'documents.newDocument': 'Nouveau document',
            'documents.titleLabel': 'Titre',
            'documents.typeLabel': 'Type de document',
            'documents.chooseType': 'Choisir un type',
            'documents.fileLabel': 'Fichier',
            'documents.descriptionLabel': 'Description',
            'documents.startDate': 'Date début',
            'documents.endDate': 'Date fin',
            'documents.clickToSelect': 'Cliquez pour sélectionner un fichier',
            'documents.fileFormats': 'PDF, Word, Excel, Images',
            'documents.change': 'Changer',
            'documents.remove': 'Supprimer',
            'compte.subscription.title': 'Abonnements',
            'common.paid': 'Payé',
            'common.unpaid': 'Non payé',
            'compte.subscription.details': 'Détails de l\'abonnement',
            'newProject.title': 'Nouveau projet',
            'newProject.subtitle': 'Remplissez les informations ci-dessous pour créer un nouveau projet immobilier.',
            'newProject.assignedManager': 'Manager assigné',
            'newProject.loading': 'Chargement des données...',
            'newProject.retry': 'Réessayer',
            'newProject.generalInfo': 'Informations générales',
            'newProject.projectType': 'Type de projet',
            'newProject.selectType': 'Sélectionner un type',
            'newProject.projectName': 'Nom du projet',
            'newProject.number': 'Numéro du projet',
            'newProject.area': 'Surface (m²)',
            'newProject.price': 'Prix total',
            'newProject.startDate': 'Date de début prévue',
            'newProject.endDate': 'Date de fin prévue',
            'newProject.address': 'Adresse',
            'newProject.numberOfLots': 'Nombre de lots',
            'newProject.numberOfRooms': 'Nombre de chambres',
            'newProject.constructionStatus': 'Statut de construction',
            'newProject.selectStatus': 'Sélectionner un statut',
            'newProject.latitude': 'Latitude',
            'newProject.longitude': 'Longitude',
            'newProject.description': 'Description du projet',
            'newProject.commonEquipment': 'Équipements communs',
            'newProject.documents': 'Documents Joints',
            'newProject.planRequired': 'Le plan (image) est requis',
            'newProject.remove': 'Retirer',
            'newProject.saving': 'Enregistrement...',
            'newProject.save': 'Créer le projet',

            // ============ PLAN SELECTION POPUP ============
            'popup.welcome.title': 'Bienvenue ! Découvrez nos plans pour créer votre premier projet',
            'popup.welcome.subtitle': 'Choisissez le plan qui correspond le mieux à vos besoins',
            'popup.welcome.close': 'Fermer',
            'popup.plan.basic.name': 'Basic',
            'popup.plan.basic.description': 'Idéal pour démarrer avec les fonctionnalités essentielles',
            'popup.plan.basic.feature1': 'Jusqu\'à 3 projets',
            'popup.plan.basic.feature2': 'Gestion basique des tâches',
            'popup.plan.basic.feature3': 'Support standard',
            'popup.plan.premium.name': 'Premium',
            'popup.plan.premium.description': 'Pour les professionnels exigeants avec toutes les fonctionnalités',
            'popup.plan.premium.feature1': 'Projets illimités',
            'popup.plan.premium.feature2': 'Gestion avancée des tâches',
            'popup.plan.premium.feature3': 'Support prioritaire 24/7',
            'popup.plan.premium.feature4': 'Rapports et analyses détaillés',
            'popup.plan.choosePlan': 'Choisir ce plan',
            'popup.plan.mostPopular': 'Le plus populaire',
            'popup.billing.monthly': 'Mensuel',
            'popup.billing.yearly': 'Annuel',
            'popup.billing.saveUpTo': 'Économisez jusqu\'à',
            'popup.billing.perMonth': '/ mois',
            'popup.billing.perMonthYearly': '/ mois (annuel)',
            'popup.loading': 'Chargement des plans...',
            'popup.error.loadPlans': 'Erreur lors du chargement des plans',
            'popup.error.payment': 'Erreur lors de l\'initialisation du paiement',
            'popup.error.loginRequired': 'Vous devez être connecté pour souscrire',

            'newProject.cancel': 'Annuler',
            'newProject.cancelConfirm': 'Êtes-vous sûr de vouloir annuler ?',
            'newProject.errors': 'Veuillez corriger les erreurs suivantes',
            'newProject.success': 'Projet créé avec succès',
            'newProject.accessDenied': 'Accès refusé',
            'newProject.required': 'Ce champ est requis',
            'newProject.minLength': 'Ce champ est trop court',
            'newProject.maxLength': 'Ce champ est trop long',
            'newProject.min': 'La valeur est trop petite',
            'newProject.dateRange': 'La date de fin doit être après la date de début',
            'newProject.fileTooLarge': 'Le fichier est trop volumineux (Max {{size}})',

            // ============ NOUVELLES TRADUCTIONS ============
            // Subcontractors
            'subcontractor.title': 'Sous-Traitants',
            'subcontractor.subtitle': 'Retrouvez en un clin d\'œil tous vos sous-traitants et partenaires.',
            'subcontractor.new': 'Nouveau Sous-Traitant',
            'subcontractor.searchPlaceholder': 'Rechercher par nom, email...',
            'subcontractor.filter': 'Filtres',
            'subcontractor.export': 'Exporter',
            'subcontractor.delete': 'Supprimer',
            'subcontractor.table.companyName': 'RAISON SOCIALE',
            'subcontractor.table.contactName': 'NOM CONTACT',
            'subcontractor.table.phone': 'TÉLÉPHONE',
            'subcontractor.table.email': 'EMAIL',
            'subcontractor.table.status': 'STATUS',
            'subcontractor.status.active': 'Actif',
            'subcontractor.status.inactive': 'Inactif',
            'subcontractor.modal.title': 'Nouveau Sous-traitant',
            'subcontractor.modal.name': 'Nom',
            'subcontractor.modal.firstName': 'Prénom',
            'subcontractor.modal.phone': 'Téléphone',
            'subcontractor.modal.email': 'Email',
            'subcontractor.modal.password': 'Mot de passe',
            'subcontractor.modal.birthDate': 'Date de naissance',
            'subcontractor.modal.birthPlace': 'Lieu de naissance',
            'subcontractor.modal.address': 'Adresse',
            'subcontractor.modal.note': 'Note: Ce sous-traitant sera automatiquement créé avec le profil "SUBCONTRACTOR"',
            'subcontractor.modal.cancel': 'Annuler',
            'subcontractor.modal.save': 'Enregistrer',
            'subcontractor.modal.saving': 'Enregistrement...',
            'subcontractor.loading': 'Chargement...',
            'subcontractor.success.add': 'Sous-traitant ajouté avec succès !',
            'subcontractor.error.add': 'Erreur lors de la création du sous-traitant',
            'subcontractor.success.delete': '{{count}} sous-traitant(s) supprimé(s) avec succès',
            'subcontractor.error.delete': '{{count}} erreur(s) lors de la suppression',
            'subcontractor.confirmDelete': 'Êtes-vous sûr de vouloir supprimer {{count}} sous-traitant(s) ?',
            'subcontractor.validation.required': 'Veuillez remplir tous les champs obligatoires',
            'subcontractor.validation.email': 'Veuillez saisir une adresse email valide',
            'subcontractor.validation.phone': 'Veuillez saisir un numéro de téléphone valide',
            'subcontractor.validation.password': 'Le mot de passe doit contenir au moins 6 caractères',
            'subcontractor.validation.selection': 'Veuillez sélectionner au moins un sous-traitant à supprimer',
            'subcontractor.pagination.viewing': 'Voir de {{start}} à {{end}} sur {{total}}',
            'subcontractor.pagination.pages': '({{total}} pages)',
            'subcontractor.export.success': 'Export réussi !',
            'En cours': 'En cours',

            // Suppliers
            'supplier.title': 'Fournisseurs',
            'supplier.subtitle': 'Gestion des Fournisseurs de votre entreprise',
            'supplier.new': 'Nouveau Fournisseur',
            'supplier.searchPlaceholder': 'Rechercher par nom',
            'supplier.table.name': 'NOM',
            'supplier.table.phone': 'TÉLÉPHONE',
            'supplier.table.email': 'EMAIL',
            'supplier.table.position': 'POSTE',
            'supplier.table.status': 'STATUS',
            'supplier.modal.title': 'Nouveau Fournisseur',
            'supplier.filter': 'Filtres',
            'supplier.export': 'Exporter',
            'supplier.delete': 'Supprimer',
            'supplier.status.active': 'Actif',
            'supplier.status.inactive': 'Inactif',
            'supplier.modal.name': 'Nom',
            'supplier.modal.firstName': 'Prénom',
            'supplier.modal.phone': 'Téléphone',
            'supplier.modal.email': 'Email',
            'supplier.modal.password': 'Mot de passe',
            'supplier.modal.birthDate': 'Date de naissance',
            'supplier.modal.birthPlace': 'Lieu de naissance',
            'supplier.modal.address': 'Adresse',
            'supplier.modal.cancel': 'Annuler',
            'supplier.modal.save': 'Enregistrer',
            'supplier.modal.saving': 'Enregistrement...',
            'supplier.loading': 'Chargement...',
            'supplier.success.add': 'Fournisseur ajouté avec succès !',
            'supplier.error.add': 'Erreur lors de la création du fournisseur',
            'supplier.success.delete': '{{count}} fournisseur(s) supprimé(s) avec succès',
            'supplier.error.delete': '{{count}} erreur(s) lors de la suppression',
            'supplier.confirmDelete': 'Êtes-vous sûr de vouloir supprimer {{count}} fournisseur(s) ?',
            'supplier.validation.required': 'Veuillez remplir tous les champs obligatoires',
            'supplier.validation.email': 'Veuillez saisir une adresse email valide',
            'supplier.validation.phone': 'Veuillez saisir un numéro de téléphone valide (min 8 chiffres)',
            'supplier.validation.password': 'Le mot de passe doit contenir au moins 6 caractères',
            'supplier.validation.selection': 'Veuillez sélectionner au moins un fournisseur à supprimer',
            'supplier.pagination.viewing': 'Voir de {{start}} à {{end}} sur {{total}}',
            'supplier.pagination.pages': '({{total}} pages)',
            'supplier.export.success': 'Export réussi !',

            // Admin Subscriptions
            'admin.subscription.title': 'Plans d\'abonnement',
            'admin.subscription.description': 'Créer, modifier ou désactiver les plans d\'abonnement proposés aux utilisateurs',
            'admin.subscription.create': 'Créer un plan',
            'admin.subscription.table.name': 'Nom du plan',
            'admin.subscription.table.cost': 'Coût total',
            'admin.subscription.table.installments': 'Échéances',
            'admin.subscription.table.projectLimit': 'Limite projets',
            'admin.subscription.table.discount': 'Remise annuelle',
            'admin.subscription.table.status': 'Statut',
            'admin.subscription.table.actions': 'Actions',
            'admin.subscription.empty': 'Aucun plan trouvé',
            'admin.subscription.status.active': 'Actif',
            'admin.subscription.status.inactive': 'Inactif',
            'admin.subscription.unlimited': 'Illimité',
            'admin.subscription.pagination.showing': 'Affichage de',
            'admin.subscription.pagination.to': 'à',
            'admin.subscription.pagination.of': 'sur',
            'admin.subscription.pagination.results': 'résultats',
            'admin.subscription.pagination.previous': 'Précédent',
            'admin.subscription.pagination.next': 'Suivant',
            'admin.subscription.loading': 'Chargement des plans...',
            'admin.subscription.error.load': 'Erreur lors du chargement des plans',
            'admin.subscription.success.delete': 'Plan "{{label}}" supprimé avec succès',
            'admin.subscription.error.delete': 'Erreur lors de la suppression du plan',
            'admin.subscription.action.view': 'Voir',
            'admin.subscription.action.edit': 'Modifier',
            'admin.subscription.searchPlaceholder': 'Rechercher par nom...',
            'admin.subscription.filter': 'Filtres',
            'admin.subscription.export': 'Exporter',
            'admin.subscription.modal.delete.title': 'Confirmer la suppression',
            'admin.subscription.modal.delete.message': 'Êtes-vous sûr de vouloir supprimer le plan "{{plan}}" ? Cette action est irréversible.',
            'admin.subscription.modal.delete.confirm': 'Supprimer',
            'admin.subscription.modal.delete.deleting': 'Suppression...',
            'admin.subscription.modal.toggle.deactivate.title': 'Désactiver le plan',
            'admin.subscription.modal.toggle.activate.title': 'Activer le plan',
            'admin.subscription.modal.toggle.deactivate.message': 'Êtes-vous sûr de vouloir désactiver le plan {{plan}} ? Cette action est irréversible et il ne sera plus visible pour les utilisateurs.',
            'admin.subscription.modal.toggle.activate.message': 'Êtes-vous sûr de vouloir activer le plan {{plan}} ? Cette action est irréversible et il sera visible pour les utilisateurs.',
            'admin.subscription.notification.deactivated': 'Plan "{{plan}}" a été désactivé.',
            'admin.subscription.notification.activated': 'Plan "{{plan}}" a été activé.',


            'stock.modals.order.title': 'Nouvelle commande de matériaux',
            'stock.modals.order.supplier': 'Fournisseur',
            'stock.modals.order.selectSupplier': 'Sélectionner un fournisseur',
            'stock.modals.order.supplierRequired': 'Veuillez sélectionner un fournisseur',
            'stock.modals.order.materialsOrdered': 'Matériaux commandés',
            'stock.modals.order.addMaterial': 'Ajouter un matériau',
            'stock.modals.order.material': 'Matériau',
            'stock.modals.order.currentStock': 'Stock actuel',
            'stock.modals.order.quantity': 'Quantité',
            'stock.modals.order.selectMaterial': 'Sélectionner un matériau',
            'stock.modals.order.cancel': 'Annuler',
            'stock.modals.order.save': 'Enregistrer',
            'stock.modals.material.title': 'Ajouter un nouveau matériau',
            'stock.modals.material.name': 'Nom du matériel',
            'stock.modals.material.currentQuantity': 'Quantité actuelle',
            'stock.modals.material.criticalThreshold': 'Seuil critique',
            'stock.modals.material.unit': 'Unité',
            'stock.modals.material.property': 'Propriété',
            'stock.modals.material.loadingUnits': 'Chargement des unités...',
            'stock.modals.material.loadingProperties': 'Chargement des propriétés...',
            'stock.modals.material.required': 'Ce champ est requis',
            'stock.modals.material.saving': 'Enregistrement...',
            'stock.modals.movement.title': 'Mouvement de stock',
            'stock.modals.movement.type': 'Type de mouvement',
            'stock.modals.movement.entry': 'Entrée',
            'stock.modals.movement.exit': 'Sortie',
            'stock.modals.movement.adjustment': 'Ajustement',
            'stock.modals.movement.selectType': 'Veuillez sélectionner un type',
            'stock.modals.movement.enterQuantity': 'Saisir la quantité',
            'stock.modals.movement.quantityError': 'La quantité doit être supérieure à 0',
            'stock.modals.movement.reference': 'Référence',
            'stock.modals.movement.referenceRequired': 'La référence est requise',
            'stock.modals.delete.title': 'Confirmer la suppression',
            'stock.modals.delete.materialMessage': 'Êtes-vous sûr de vouloir supprimer {{label}} ?',
            'stock.modals.delete.orderMessage': 'Êtes-vous sûr de vouloir supprimer la commande {{id}} ?',
            'stock.modals.delete.warning': 'Cette action est irréversible et supprimera également l\'historique des mouvements associés.',
            'stock.modals.delete.confirm': 'Supprimer',
            'stock.modals.delete.deleting': 'Suppression...',
            'stock.alerts.title': 'Alertes stock matériaux',
            'stock.alerts.none': 'Il n\'y a pas d\'alerte pour le moment',
            'stock.charts.consumption': 'Consommation mensuelle',
            'stock.charts.top5': 'Top 5 matériaux les plus utilisés',
            'stock.charts.evolution': 'Évolution mensuelle',

            'stock.orderStatus.pending': 'En attente',
            'stock.orderStatus.confirmed': 'Confirmée',
            'stock.orderStatus.inProgress': 'En cours',
            'stock.orderStatus.delivery': 'Livrée',
            'stock.orderStatus.delivered': 'Livrée',
            'stock.orderStatus.cancelled': 'Annulée',
            'stock.orderStatus.approved': 'Approuvée',
            'stock.orderStatus.partiallyDelivered': 'Partiellement livrée',
            'stock.success.materialDeleted': '{{name}} a été supprimé avec succès',
            'stock.error.accessDenied': 'Accès refusé - Vous n\'avez pas les permissions nécessaires',
            'stock.error.deleteConflict': 'Impossible de supprimer ce matériel car il est lié à des commandes',
            'stock.error.deleteFailed': 'Erreur lors de la suppression du matériel',
            'stock.error.cannotEditOrder': 'Impossible de modifier une commande livrée ou annulée',
            'stock.error.cannotCancelOrder': 'Impossible d\'annuler une commande déjà livrée ou annulée',
            'stock.error.noProof': 'Aucune preuve disponible pour cette livraison',

            // ============ DEMANDES COMPONENT ============
            'demande.title': 'Demandes',
            'demande.subtitle': 'Suivez et gérez vos demandes auprès des Bureaux d\'Études (BET)',
            'demande.searchPlaceholder': 'Rechercher par titre, description...',
            'demande.period': 'Période',
            'demande.steps.pendingDetails': 'Détails en attente',
            'demande.steps.delivered': 'Livrée',
            'demande.modals.report.title': 'Rapport',
            'demande.modals.report.subjectLabel': 'Sujet',
            'demande.modals.report.versionLabel': 'Version',
            'demande.modals.report.documentLabel': 'Document',
            'demande.modals.report.versionPlaceholder': 'Version',
            'demande.modals.report.subjectPlaceholder': 'Sujet',
            'demande.modals.report.documentPlaceholder': 'Document',
            'common.chooseFile': 'Choisir un fichier',
            'demande.details.reports': 'Rapports',
            'demande.pagination.showing': 'Affichage',
            'demande.pagination.of': 'de',
            'demande.pagination.total': 'Total',
            'demande.pagination.page': 'Page',
            'demande.pagination.to': 'à',
            'demande.pagination.from': 'de',
            'demande.pagination.results': 'résultats',
            'demande.actions.viewDetails': 'Voir les détails',
            'common.send': 'Envoyer',
            'settings.unitOfMeasure': 'Unité de mesure',
            'settings.documents': 'Documents',
            'settings.propertyType': 'Type de Bien',
            'settings.materialCategories': 'Catégories de matériel',
            'demande.details.addCommentPlaceholder': 'Ajouter un commentaire',
            'demande.period.week': 'Cette semaine',
            'demande.period.month': 'Ce mois',
            'demande.period.quarter': 'Ce trimestre',
            'demande.status': 'Statut',
            'demande.status.PENDING': 'En attente',
            'demande.status.IN_PROGRESS': 'En cours',
            'demande.status.DELIVERED': 'Livrée',
            'demande.status.VALIDATED': 'Validée',
            'demande.status.REJECTED': 'Rejetée',
            'demande.table.title': 'Titre',
            'demande.table.description': 'Description',
            'demande.table.bet': 'BET',
            'demande.table.createdAt': 'Date de création',
            'demande.table.status': 'Statut',
            'demande.table.actions': 'Actions',
            'demande.empty': 'Aucune demande trouvée',
            'demande.newRequest': 'Nouvelle demande',
            'demande.details.title': 'Détails de la demande',
            'demande.details.downloadReport': 'Télécharger le rapport',
            'demande.details.noReports': 'Aucun rapport disponible',
            'demande.details.comments': 'Commentaires',
            'demande.details.noComments': 'Aucun commentaire pour le moment',
            'demande.details.send': 'Envoyer',
            'demande.details.writeComment': 'Écrire un commentaire...',
            'demande.actions.validate': 'Valider',
            'demande.actions.reject': 'Rejeter',
            'demande.actions.delete': 'Supprimer',
            'demande.actions.createReport': 'Créer un rapport',
            'demande.modals.validate.title': 'Valider la demande',
            'demande.modals.validate.message': 'Êtes-vous sûr de vouloir valider cette demande ?',
            'demande.modals.reject.title': 'Rejeter la demande',
            'demande.modals.reject.message': 'Êtes-vous sûr de vouloir rejeter cette demande ?',
            'demande.modals.reject.reason': 'Motif du rejet',
            'demande.modals.reject.reasonPlaceholder': 'Saisissez le motif...',
            'demande.modals.delete.title': 'Supprimer la demande',
            'demande.modals.delete.message': 'Êtes-vous sûr de vouloir supprimer cette demande ?',
            'demande.modals.createReport.title': 'Nouveau rapport',
            'demande.modals.createReport.reportTitle': 'Titre du rapport',
            'demande.modals.createReport.version': 'Version',
            'demande.modals.createReport.file': 'Fichier',
            'demande.steps.pending': 'En attente de réponse',
            'demande.steps.inProgress': 'En cours d\'acceptation',
            'demande.steps.delivery': 'En cours de livraison',
            'demande.steps.validation': 'Validation/Rejet',
            'demande.success.validated': 'Demande validée avec succès',
            'demande.success.rejected': 'Demande rejetée avec succès',
            'demande.success.deleted': 'Demande supprimée avec succès',
            'demande.success.commentAdded': 'Commentaire ajouté avec succès',
            'demande.success.reportCreated': 'Rapport créé avec succès',
            'demande.error.load': 'Erreur lors du chargement des demandes',
            'demande.error.action': 'Erreur lors de l\'action',
            'demande.error.fileRequired': 'Veuillez sélectionner un fichier',
            'demande.error.fieldsRequired': 'Veuillez remplir tous les champs obligatoires',
            // Aliases for DemandeComponent
            'demande.header.title': 'Demandes',
            'demande.header.description': 'Suivez et gérez vos demandes auprès des Bureaux d\'Études Techniques (BET)',
            'demande.filters.searchPlaceholder': 'Rechercher par titre, description...',
            'demande.filters.period': 'Période',
            'demande.details.subject': 'Objet',
            'demande.details.description': 'Description',
            'demande.details.bet': 'BET',
            'demande.details.createdAt': 'Date de création',
            'demande.details.status': 'Statut',
            'demande.details.actions': 'Actions',
            'demande.filters.thisWeek': 'Cette semaine',
            'demande.filters.thisMonth': 'Ce mois',
            'demande.filters.thisQuarter': 'Ce trimestre',
            'demande.filters.status': 'Statut',
            'demande.filters.dateCreated': 'Date de création',
            'demande.loading': 'Chargement des demandes...',
            'demande.empty.title': 'Aucune demande',
            'demande.empty.message': 'Aucune demande trouvée pour le moment.',

            // ============ ÉTUDE BET COMPONENT ============
            // Modal: Nouvelle étude
            'studyBet.modal.newStudy': 'Nouvelle étude',
            'studyBet.modal.editStudy': 'Modifier l\'étude',
            'studyBet.modal.newReport': 'Nouveau rapport',
            'studyBet.modal.selectBET': 'Sélection BET',
            'studyBet.modal.selectBETPlaceholder': 'Sélectionner un BET',
            'studyBet.modal.title': 'Titre',
            'studyBet.modal.titleLabel': 'Titre',
            'studyBet.modal.description': 'Description',
            'studyBet.modal.descriptionLabel': 'Description',
            'studyBet.modal.version': 'Version',
            'studyBet.modal.versionLabel': 'Version',
            'studyBet.modal.document': 'Document',
            'studyBet.modal.documentLabel': 'Document',
            'studyBet.modal.enterPlaceholder': 'Saisir',
            'studyBet.modal.loadingBETs': 'Chargement des BETs...',
            'studyBet.modal.sending': 'Envoi en cours...',
            'studyBet.modal.acceptedFormats': 'Formats acceptés : PDF, Word, Excel',
            'studyBet.modal.validateQuestion': 'Valider la demande ?',
            'studyBet.modal.validateMessage': 'Cette demande passera au statut Validée',
            'studyBet.modal.rejectQuestion': 'Refuser la demande ?',
            'studyBet.modal.rejectMessage': 'La demande sera marquée Rejetée.',
            'studyBet.modal.rejectReasonPrompt': 'Vous pouvez ajouter un motif',
            'studyBet.modal.rejectReasonPlaceholder': 'Motif du rejet...',

            // Detail Modal
            'studyBet.details.information': 'Informations',
            'studyBet.details.associatedReports': 'Rapports associés',
            'studyBet.details.comments': 'Commentaires',
            'studyBet.details.noComments': 'Aucun commentaire',
            'studyBet.details.noReports': 'Aucun rapport',
            'studyBet.details.concernedBET': 'BET concerné',
            'studyBet.details.creationDate': 'Date de création',
            'studyBet.details.state': 'État',
            'studyBet.details.close': 'Fermer',
            'studyBet.details.addReport': 'Ajouter un rapport',
            'studyBet.details.downloadReport': 'Télécharger',
            'studyBet.details.reportSubmitted': 'Soumis le',

            // Progress Steps
            'studyBet.progress.step1': 'En attente',
            'studyBet.progress.step2': 'En cours',
            'studyBet.progress.step3': 'Livrée',
            'studyBet.progress.step4': 'Validée',

            // ============ LOTS COMPONENT ============
            // Modal: Nouveau lot
            'lots.modal.designation': 'Désignation du lot',
            'lots.modal.description': 'Description',
            'lots.modal.startDate': 'Date de début prévue',
            'lots.modal.endDate': 'Date de fin prévue',
            'lots.modal.assignSubcontractor': 'Affecter un sous-traitant',
            'lots.modal.update': 'Mettre à jour',
            'lots.modal.subcontractorPlaceholder': 'Mme. Sarr (Électricité Pro)',
            'lots.modal.designationPlaceholder': 'Gros œuvre',
            'lots.modal.descriptionPlaceholder': 'Fondations, maçonnerie, béton armé, charpente',

            // Card labels
            'lots.card.expectedDate': 'Date prévue',
            'lots.card.expectedEndDate': 'Fin prévue',

            // Details modal
            'lots.details.noDocuments': 'Aucun document pour le moment',
            'lots.details.addDocument': 'Ajouter un document',
            'lots.details.documentsAttached': 'Documents joints',

            // ============ COMPTE COMPONENT (Complement) ============


            // Incidents / Etude-BET (Ajouts)
            'incidents.status': 'Statut',
            'incidents.statusUpdated': 'Statut mis à jour avec succès',
            'incidents.statusUpdateError': 'Erreur lors du changement de statut',
            'incidents.statusPending': 'En attente',
            'incidents.statusOpen': 'Ouvert',
            'incidents.statusInProgress': 'En cours',
            'incidents.statusResolved': 'Résolu',
            'incidents.statusClosed': 'Fermé',
            'studyBet.modal.objective': 'Objectif',
            'studyBet.modal.objectivePlaceholder': "Décrivez l'objectif de l'étude...",
            'studyBet.modal.problemObserved': 'Problème observé',
            'studyBet.modal.problemObservedPlaceholder': 'Décrivez le problème constaté...',
            'studyBet.modal.studyType': "Type d'étude",
            'studyBet.modal.selectType': "Sélectionner un type",
            'studyBet.modal.attachedDocuments': "Documents joints",
            'studyBet.modal.addDocument': "Ajouter un document",
            'studyBet.modal.type': "Type",
            'studyBet.modal.noDocumentAdded': "Aucun document ajouté. Cliquez sur Ajouter un document pour en joindre.",

            // IA Report Modal
            'studyBet.ia.title': 'Rapport IA',
            'studyBet.ia.loading': 'Chargement du rapport IA...',
            'studyBet.ia.errorRetry': 'Veuillez réessayer ultérieurement.',
            'studyBet.ia.severity': 'Sévérité',
            'studyBet.ia.observations': 'Observations',
            'studyBet.ia.recommendations': 'Recommandations',
            'studyBet.ia.conclusion': 'Conclusion',
            'studyBet.ia.analyzedDocs': 'Documents analysés',
            'studyBet.ia.document': 'Document IA',

            // ============ ATTENDANCE REPORT ============
            'attendanceReport.title': 'Rapport de présence',
            'attendanceReport.loading': 'Chargement du rapport...',
            'attendanceReport.retry': 'Réessayer',
            'attendanceReport.noData': 'Aucun travailleur trouvé',
            'attendanceReport.noDataHint': 'Aucune donnée de présence disponible pour cette période.',
            'attendanceReport.exportExcel': 'Exporter Excel',
            'attendanceReport.period': 'Période',
            'attendanceReport.totalWorkingDays': 'Jours ouvrés totaux',
            'attendanceReport.pastWorkingDays': 'Jours ouvrés passés',
            'attendanceReport.remainingWorkingDays': 'Jours restants',
            'attendanceReport.workersTable': 'Détail par travailleur',
            'attendanceReport.fullName': 'Nom complet',
            'attendanceReport.daysPresent': 'Jours présents',
            'attendanceReport.leaveDays': 'Congés',
            'attendanceReport.absentDays': 'Absences',
            'attendanceReport.salaryDeduction': 'Déduction salaire',
            'attendanceReport.presence': 'Présence',
            'attendanceReport.legendGood': 'Bon',
            'attendanceReport.legendWarning': 'Attention',
            'attendanceReport.legendCritical': 'Critique',
        },
        EN: {
            'stock.orderDetails': 'Order Details',
            'sidebar.dashboard': 'Dashboard',
            'sidebar.dashboardAdmin': 'Admin Dashboard',
            'sidebar.dashboardStudy': 'Study Dashboard',
            'sidebar.users': 'Users',
            'sidebar.subscriptions': 'Subscription Plans',
            'sidebar.subcontractors': 'Subcontractors',
            'sidebar.suppliers': 'Suppliers',
            'team.tasksTodo': 'To Do',
            'team.tasksInProgress': 'In Progress',
            'team.tasksBlocked': 'Blocked',
            'team.tasksDone': 'Done',
            'team.tasksLate': 'Late',
            'sidebar.settings': 'Settings',
            'sidebar.unitMeasure': 'Unit of Measure',
            'sidebar.documents': 'Documents',
            'sidebar.propertyType': 'Property Type',
            'sidebar.materialCategories': 'Material Categories',
            'sidebar.projects': 'Projects',
            'sidebar.requests': 'Requests',
            'sidebar.orders': 'Orders',
            'sidebar.myAccount': 'My Account',
            'sidebar.logout': 'Sign out',
            'sidebar.reclamations': 'Claims',
            'breadcrumb.helpSupport': 'Help & Support',
            'breadcrumb.language': 'Language',
            'breadcrumb.home': 'Home',
            'breadcrumb.dashboard': 'Dashboard',
            

            // ============ AUTHENTICATION ============
            // Login Page
            'auth.login.title': 'Welcome to UKUBHALISA.',
            'auth.login.subtitle': 'Simplified attendance tracking and site management system!',
            'auth.login.emailLabel': 'Username or Email Address',
            'auth.login.emailPlaceholder': 'Enter your username',
            'auth.login.passwordLabel': 'Password',
            'auth.login.passwordPlaceholder': 'Enter your password (6 characters min)',
            'auth.login.showPassword': 'Show password',
            'auth.login.hidePassword': 'Hide password',
            'auth.login.forgotPassword': 'Forgot password?',
            'auth.login.createAccount': 'Create an account',
            'auth.login.loginButton': 'Sign in',
            'auth.login.loggingIn': 'Signing in...',
            'auth.login.backToHome': 'Back to home',
            'auth.login.selectLanguage': 'Select language',

            // Login Errors
            'auth.login.error.required': 'Email or phone number is required',
            'auth.login.error.invalidFormat': 'Invalid format. Use a valid email or phone number in format 7XXXXXXXX (ex: 771234567)',
            'auth.login.error.passwordRequired': 'Password is required',
            'auth.login.error.passwordMinLength': 'Password must contain at least 6 characters',
            'auth.login.error.invalidCredentials': 'Incorrect email/phone or password',
            'auth.login.error.serverError': 'Server error. Please try again later.',
            'auth.login.error.networkError': 'Unable to connect to server. Check your internet connection.',
            'auth.login.error.accessDenied': 'Access denied. Your account may be suspended.',
            'auth.login.error.formErrors': 'Please fix the errors in the form',

            // Login Success
            'auth.login.success.welcome': 'Login successful! Welcome to your space.',
            'auth.login.success.welcomeAdmin': 'Login successful! Welcome to your admin dashboard.',
            'auth.login.success.welcomeBET': 'Login successful! Welcome to your BET dashboard.',
            'auth.login.success.welcomeSupplier': 'Login successful! Welcome to your supplier dashboard.',
            'auth.login.warning.noSubscription': 'Please subscribe to access the dashboard.',
            'auth.login.error.subscriptionCheck': 'Error checking your subscription. Please try again.',

            // Register Page
            'auth.register.title': 'Create an Account',
            'auth.register.subtitle': 'Join UKUBHALISA and manage your construction sites efficiently',
            'auth.register.firstNameLabel': 'First Name',
            'auth.register.firstNamePlaceholder': 'Enter your first name',
            'auth.register.lastNameLabel': 'Last Name',
            'auth.register.lastNamePlaceholder': 'Enter your last name',
            'auth.register.emailLabel': 'Email Address',
            'auth.register.emailPlaceholder': 'example@email.com',
            'auth.register.phoneLabel': 'Phone Number',
            'auth.register.phonePlaceholder': '771234567',
            'auth.register.passwordLabel': 'Password',
            'auth.register.passwordPlaceholder': 'Minimum 6 characters',
            'auth.register.confirmPasswordLabel': 'Confirm Password',
            'auth.register.confirmPasswordPlaceholder': 'Re-enter your password',
            'auth.register.profileLabel': 'Profile',
            'auth.register.profilePlaceholder': 'Select your profile',
            'auth.register.companyLabel': 'Company',
            'auth.register.companyPlaceholder': 'Your company name',
            'auth.register.addressLabel': 'Address',
            'auth.register.addressPlaceholder': 'Your full address',
            'auth.register.registerButton': 'Sign up',
            'auth.register.registering': 'Signing up...',
            'auth.register.alreadyHaveAccount': 'Already have an account?',
            'auth.register.loginLink': 'Sign in',
            'auth.register.termsAccept': 'I accept the terms of use',

            // Register Errors
            'auth.register.error.firstNameRequired': 'First name is required',
            'auth.register.error.lastNameRequired': 'Last name is required',
            'auth.register.error.emailRequired': 'Email is required',
            'auth.register.error.emailInvalid': 'Invalid email format',
            'auth.register.error.phoneRequired': 'Phone number is required',
            'auth.register.error.phoneInvalid': 'Invalid phone format (ex: 771234567)',
            'auth.register.error.passwordRequired': 'Password is required',
            'auth.register.error.passwordMinLength': 'Password must contain at least 6 characters',
            'auth.register.error.confirmPasswordRequired': 'Please confirm your password',
            'auth.register.error.passwordMismatch': 'Passwords do not match',
            'auth.register.error.profileRequired': 'Please select a profile',
            'auth.register.error.termsRequired': 'You must accept the terms of use',
            'auth.register.error.emailExists': 'This email is already in use',
            'auth.register.error.phoneExists': 'This phone number is already in use',


            // Register Success
            'auth.register.success.accountCreated': 'Account created successfully!',
            'auth.register.success.redirecting': 'Redirecting to login page...',

            // Reset Password
            'auth.reset.title': 'Reset Password',
            'auth.reset.subtitle': 'Enter your email to receive a reset link',
            'auth.reset.emailLabel': 'Email Address',
            'auth.reset.emailPlaceholder': 'example@email.com',
            'auth.reset.sendButton': 'Send Link',
            'auth.reset.sending': 'Sending...',
            'auth.reset.backToLogin': 'Back to login',
            'auth.reset.success': 'A reset email has been sent',
            'auth.reset.error.emailRequired': 'Email is required',
            'auth.reset.error.emailInvalid': 'Invalid email format',
            'auth.reset.error.emailNotFound': 'No account associated with this email',
            'auth.reset.error.serverError': 'Error sending email',

            'profile.siteManager': 'Manager',
            'profile.subcontractor': 'Subcontractor',
            'profile.supplier': 'Supplier',
            'profile.admin': 'Administrator',
            'profile.bet': 'Technical Office',
            'profile.user': 'User',
            'profile.promoter': 'Promoter',
            'profile.moa': 'Project Owner',
            'profile.allRoles': 'All Roles',
            'profile.notaire': 'Notary',
            'profile.reservataire': 'Buyer',
            'profile.bank': 'Bank',
            'profile.agency': 'Agency',
            'profile.proprietaire': 'Owner',
            'profile.syndic': 'Property Manager',
            'profile.locataire': 'Tenant',
            'profile.prestataire': 'Service Provider',
            'profile.tom': 'TOM',
            'profile.worker': 'Employee',

            // ============ ADMIN PAGES ============
            // Subscription Plans
            'subscriptions.plansTitle': 'Subscription Plans',
            'subscriptions.createPlan': 'Create Plan',
            'subscriptions.editPlan': 'Edit Plan',
            'subscriptions.deletePlan': 'Delete Plan',
            'subscriptions.planName': 'Plan Name',
            'subscriptions.monthlyPrice': 'Monthly Price',
            'subscriptions.yearlyPrice': 'Yearly Price',
            'subscriptions.features': 'Features',
            'subscriptions.table.name': 'Name',
            'subscriptions.table.price': 'Price',
            'subscriptions.table.status': 'Status',
            'subscriptions.table.actions': 'Actions',
            'subscriptions.success.created': 'Plan created successfully',
            'subscriptions.success.updated': 'Plan updated successfully',
            'subscriptions.success.deleted': 'Plan deleted successfully',

            // Subcontractors
            'subcontractors.title': 'Subcontractor Management',
            'subcontractors.addSubcontractor': 'Add Subcontractor',
            'subcontractors.companyName': 'Company Name',
            'subcontractors.contactName': 'Contact Name',
            'subcontractors.email': 'Email',
            'subcontractors.phone': 'Phone',
            'subcontractors.address': 'Address',
            'subcontractors.specialty': 'Specialty',
            'subcontractors.table.company': 'Company',
            'subcontractors.table.contact': 'Contact',
            'subcontractors.table.specialty': 'Specialty',
            'subcontractors.table.actions': 'Actions',
            'subcontractors.success.created': 'Subcontractor added successfully',

            // Suppliers
            'suppliers.title': 'Supplier Management',
            'suppliers.addSupplier': 'Add Supplier',
            'suppliers.companyName': 'Company Name',
            'suppliers.contactName': 'Contact Name',
            'suppliers.email': 'Email',
            'suppliers.phone': 'Phone',
            'suppliers.category': 'Category',
            'suppliers.table.company': 'Company',
            'suppliers.table.contact': 'Contact',
            'suppliers.table.category': 'Category',
            'suppliers.table.actions': 'Actions',
            'suppliers.success.created': 'Supplier added successfully',

            // Supplier Dashboard
            'supplier.dashboard.title': 'Supplier Dashboard',
            'supplier.dashboard.refresh': 'Refresh',
            'supplier.dashboard.stats.pending': 'Pending',
            'supplier.dashboard.stats.approved': 'Approved',
            'supplier.dashboard.stats.inDelivery': 'In Delivery',
            'supplier.dashboard.stats.delivered': 'Delivered',
            'supplier.dashboard.stats.rejected': 'Rejected',
            'supplier.dashboard.charts.distribution': 'Order Distribution (%)',
            'supplier.dashboard.charts.topProperties': 'Top Properties',
            'supplier.dashboard.period.thisMonth': 'This Month',
            'supplier.dashboard.period.thisQuarter': 'This Quarter',
            'supplier.dashboard.period.thisYear': 'This Year',
            'supplier.dashboard.noData': 'No data available',
            'supplier.dashboard.error': 'Error loading data',

            // Supplier Orders
            'supplier.orders.title': 'My Orders',
            'supplier.orders.createQuote': 'Create Quote',
            'supplier.orders.viewDetails': 'View Details',
            'supplier.orders.orderNumber': 'Order No.',
            'supplier.orders.date': 'Date',
            'supplier.orders.property': 'Property',
            'supplier.orders.status': 'Status',
            'supplier.orders.actions': 'Actions',
            'supplier.orders.noOrders': 'No orders available',

            // Settings
            'settings.title': 'Settings',
            'settings.units.title': 'Units of Measure',
            'settings.units.add': 'Add Unit',
            'settings.units.name': 'Unit Name',
            'settings.units.symbol': 'Symbol',
            'settings.documents.title': 'Document Types',
            'settings.propertyTypes.title': 'Property Types',
            'settings.materialCategories.title': 'Material Categories',

            'common.search': 'Search',
            'common.searchPlaceholder': 'Search...',
            'common.filter': 'Filters',
            'common.export': 'Export',
            'common.add': 'Add',
            'common.edit': 'Edit',
            'common.delete': 'Delete',
            'common.save': 'Save',
            'common.cancel': 'Cancel',
            'common.close': 'Close',
            'common.next': 'Next',
            'common.previous': 'Previous',
            'common.loading': 'Loading...',
            'common.noData': 'No data',
            'common.actions': 'Actions',
            'common.status': 'Status',
            'common.date': 'Date',
            'common.view': 'View',
            'common.copy': 'Copy',
            'common.confirm': 'Confirm',
            'common.yes': 'Yes',
            'common.no': 'No',
            'common.active': 'Active',
            'common.inactive': 'Inactive',
            'common.all': 'All',
            'common.create': 'Create',
            'common.update': 'Update',
            'common.download': 'Download',
            'common.upload': 'Upload',
            'common.back': 'Back',
            'common.details': 'Details',
            'common.total': 'Total',
            'common.showing': 'Showing',
            'common.to': 'to',
            'common.of': 'of',
            'common.results': 'results',
            'common.generalInfo': 'General Information',
            'common.selectType': 'Select a type',
            'common.selectLevel': 'Select a level',
            'common.level': 'Level',
            'common.totalCost': 'Total Cost',
            'common.projectLimit': 'Project Limit',
            'common.description': 'Description',
            'common.saving': 'Saving...',
            'common.accept': 'Accept',
            'common.reject': 'Reject',

            'common.refuse': 'Refuse',
            'common.planCreatedMessage': 'The plan "{{plan}}" has been created.',
            'common.planUpdatedMessage': 'The plan "{{plan}}" has been updated.',
            'notifications.title': 'Notifications',

            'notifications.newUser': 'New user registered',
            'notifications.newUserMessage': '{{name}} just registered',
            'notifications.noNotifications': 'No notifications',
            'notifications.markAllRead': 'Mark all as read',
            'dashboard.title': 'Dashboard',
            'dashboard.overview': 'Construction Sites Overview',
            'status.inProgress': 'In Progress',
            'projectStatus.planning': 'Planning',
            'projectStatus.execution': 'Execution',
            'projectStatus.suspended': 'Suspended',
            'projectStatus.cancelled': 'Cancelled',
            'status.planned': 'Planned',
            'equipment.wasteDisposal': 'Waste Disposal',
            'equipment.mezzanine': 'Mezzanine',
            'status.delayed': 'Delayed',
            'status.pending': 'Pending',
            'status.completed': 'Completed',
            'users.title': 'User Management',
            'help.title': 'Help & Support',
            'help.description': 'Contact us for any questions or assistance. We are here to help!',
            'help.email': 'Email',
            'help.phone': 'Phone',
            'help.hours': 'Support hours:',
            'help.hoursValue': 'Monday - Friday, 08:00 AM - 06:00 PM',

            // Compte
            'compte.title': 'My Account',
            'compte.tabs.info': 'Personal Information',
            'compte.tabs.subscriptions': 'Subscriptions',
            'compte.tabs.invoices': 'Invoices',
            'compte.form.firstName': 'First Name',
            'compte.form.lastName': 'Last Name',
            'compte.form.phone': 'Phone Number',
            'compte.form.email': 'Email',
            'compte.form.address': 'Address',
            'compte.form.company': 'Company Name',
            'compte.form.profile': 'Profile',
            'compte.form.latitude': 'Latitude',
            'compte.form.longitude': 'Longitude',
            'compte.form.qrcode': 'QR Code',
            'compte.form.name': 'Name',
            'compte.form.property': 'Property',
            'compte.form.realEstatePropertyId': 'Real Estate Property ID',
            'compte.form.realEstateProperty': 'Real Estate Property',
            'compte.form.id': 'ID',
            'compte.form.createdAt': 'Created At',
            'compte.form.updatedAt': 'Updated At',
            'compte.form.deletedAt': 'Deleted At',
            'compte.form.deleted': 'Deleted',
            'compte.form.deletedBy': 'Deleted By',
            'compte.form.deletedReason': 'Deleted Reason',
            'compte.profile.cantEdit': 'Profile cannot be modified',
            'compte.photo.new': 'New Photo',
            'compte.photo.reminder': 'Don\'t forget to click "Update" to save your new photo.',
            'compte.subscription.current': 'My Subscription',
            'compte.subscription.expire': 'Expiration Date',
            'compte.subscription.viewOffers': 'View My Offers',
            'compte.subscription.detail': 'Subscription Detail',
            'compte.subscription.planName': 'Plan Name',
            'compte.subscription.subtitle': 'Subtitle',
            'compte.subscription.paymentStatus': 'Payment Status',
            'compte.subscription.paid': 'Paid',
            'compte.subscription.period': 'Period',
            'compte.subscription.amountPaid': 'Amount Paid',
            'compte.subscription.paymentMethod': 'Payment Method',
            'compte.subscription.benefits': 'Included Benefits',
            'compte.subscription.monthly': 'Monthly',
            'compte.subscription.yearly': 'Yearly',
            'compte.subscription.title': 'Subscription Plans',
            'compte.subscription.details': 'Choose Your Subscription Plan',
            'compte.subscription.description': 'Select the plan that best suits your needs and enjoy all the features we offer to manage your construction projects efficiently.',
            'compte.subscription.selectPlan': 'Select Plan',
            'compte.subscription.subscribe': 'Subscribe',
            'compte.subscription.upgrade': 'Upgrade to Premium',
            'compte.subscription.includedBenefits': 'Included Benefits',
            'compte.subscription.maxProjects': 'Maximum Projects',
            'compte.subscription.prioritySupport': 'Priority Support',
            'compte.subscription.unlimitedProjects': 'Unlimited Projects',
            'compte.subscription.save': 'Save {{percent}}%',
            'compte.subscription.perMonth': 'month',
            'compte.subscription.perMonthYearly': 'month (billed yearly)',
            'compte.subscription.processing': 'Processing...',
            'common.print': 'Print',
            'compte.invoices.searchPlaceholder': 'Search for an invoice...',
            'compte.invoices.search': 'Search here',
            'compte.invoices.allStatus': 'All Statuses',
            'compte.invoices.active': 'Active',
            'compte.invoices.expired': 'Expired',
            'compte.invoices.reference': 'Reference',
            'compte.invoices.amount': 'Amount',
            'compte.invoices.plan': 'Plan',
            'compte.invoices.status': 'Status',
            'compte.invoices.pending': 'Pending',
            'compte.invoices.payNow': 'Pay Now',
            'compte.invoices.noInvoices': 'No invoices available',
            'compte.invoices.table.description': 'Description',
            'compte.invoices.table.period': 'Period',
            'compte.invoices.table.amountHT': 'Amount (Excl. Tax)',
            'compte.save.loading': 'Saving...',

            // Dashboard
            'dashboard.loading.title': 'Loading Dashboard',
            'team.roles.worker': 'salaried',
            'Exit': 'Exit',
            
            'dashboard.loading.subtitle': 'Analyzing data, please wait...',
            'dashboard.error.title': 'Unable to load data',
            'dashboard.retry': 'Retry',
            'dashboard.searchProject': 'Search by project',
            'dashboard.overview.sites': 'Construction Sites Overview',
            'dashboard.overview.studies': 'Studies Overview',
            'dashboard.studies.distribution': 'Studies Distribution (%)',
            'dashboard.studies.completed': 'Completed',
            'dashboard.studies.initiated': 'In Progress',
            'dashboard.studies.notInitiated': 'Pending',
            'dashboard.indicators.progress': 'Average Progress Rate',
            'dashboard.indicators.budget': 'Global Budget',
            'dashboard.budget.consumed': 'Consumed',
            'dashboard.budget.remaining': 'Remaining',
            'dashboard.alerts.stock': 'Stock Alerts',
            'dashboard.alerts.none': 'No alerts',
            'dashboard.charts.progress': 'Progress Status',
            'dashboard.charts.incidents': 'Reported Incidents (7 days)',
            'dashboard.tasks.critical': 'Critical Tasks Due',
            'dashboard.photos.recent': 'Recent Photos',
            'dashboard.performance.summary': 'Performance Summary',
            'dashboard.performance.avgPresence': 'Average Presence',
            'dashboard.performance.lateTasks': 'Late Tasks',
            'dashboard.performance.alertMaterials': 'Materials in Alert',
            'dashboard.rate.avg': 'Average Rate',
            'dashboard.metrics.inProgress': 'In Progress',
            'dashboard.metrics.delayed': 'Delayed',
            'dashboard.metrics.pending': 'Pending',
            'dashboard.metrics.completed': 'Completed',
            'dashboard.metrics.total': 'Total',
            'dashboard.metrics.delivered': 'Delivered',
            'dashboard.metrics.budgetConsumed': 'Budget Consumed',
            'dashboard.metrics.incidents7d': 'Incidents (7d)',

            // Common / Other
            'common.month.yearly': 'month (yearly)',
            'common.month': 'month',
            'common.year': 'year',
            'common.updateSave': 'Update',
            'common.viewDetails': 'View Details',

            'common.processing': 'Processing...',
            'common.economize': 'Save',
            'common.unlimitedProjects': 'Unlimited Projects',
            'common.maxProjects': 'project(s) maximum',
            'common.prioritySupport': 'Priority Support',
            'common.installments': 'Payment in {{count}} installments',
            'common.yearlyDiscount': 'discount on yearly payment',
            'common.popular': 'POPULAR',
            'common.changePhoto': 'Change Photo',
            'Tous les roles': 'All Roles',
            // Generic Section Headers
            'requests.title': 'Requests',
            'orders.title': 'Orders',
            'subscriptions.title': 'Subscriptions',
            'reclamations.title': 'Claims',


            // Reclamations
            'reclamations.pageTitle': 'Claims Management',
            'reclamations.subtitle': 'Create, modify or disable subscription plans offered to users',
            'reclamations.searchPlaceholder': 'Search by user, subject...',
            'reclamations.filters': 'Filters',
            'reclamations.export': 'Export',
            'reclamations.table.user': 'User',
            'reclamations.table.subject': 'Subject',
            'reclamations.table.category': 'Category',
            'reclamations.table.status': 'Status',
            'reclamations.table.date': 'Date',
            'reclamations.table.actions': 'Actions',
            'reclamations.status.inProgress': 'In Progress',
            'reclamations.status.resolved': 'Resolved',
            'reclamations.status.pending': 'Pending',
            'reclamations.pagination.showing': 'Showing',
            'reclamations.pagination.to': 'to',
            'reclamations.pagination.on': 'of',
            'reclamations.pagination.results': 'results',
            'reclamations.pagination.next': 'Next',

            // Users Page
            'users.subtitle': 'Create, edit, view, search, filter registered users',
            'users.table.user': 'User',
            'users.table.registerDate': 'Registration Date',
            'users.create': 'New User',
            'users.deleteConfirm': 'Are you sure you want to delete this user?',

            // Export
            'export.select': 'Export Format',
            'export.excel': 'Export',
            'export.pdf': 'Export to PDF',
            'export.success': 'Export success',
            'export.error': 'Export error',

            // Projects
            'projects.title': 'Ongoing Projects',
            'projects.description': 'View the list of your current construction sites and follow their progress in real time.',
            'projects.newProject': 'New Project',
            'projects.searchByProject': 'Search by project',
            'projects.allPeriods': 'All periods',
            'projects.thisYear': 'This year',
            'projects.lastYear': 'Last year',
            'projects.older': 'Older',
            'projects.allStatuses': 'All statuses',
            'projects.active': 'Active',
            'projects.inactive': 'Inactive',
            'projects.completed': 'Completed',
            'projects.inProgress': 'In progress',
            'projects.progress': 'Progress',
            'projects.loadingProjects': 'Loading projects...',
            'projects.noProjectFound': 'No project found',
            'projects.retry': 'Retry',
            'projects.perPage': 'Projects per page:',
            'projects.page': 'Page',
            'projects.on': 'of',
            'projects.noImage': 'No image',
            'projects.blocked': 'BLOCKED',
            'projects.blockedTitle': 'Blocked Project',
            'projects.blockedRestricted': 'Restricted Access',
            'projects.blockedMessage': 'This project is blocked until your subscription renewal. Please update your plan to access it.',
            'projects.subscriptions': 'Subscriptions',
            'projects.showing': 'Showing',
            'projects.export': 'Export',

            // Project Detail Tabs
            'projectDetail.presentation': 'Presentation',
            'projectDetail.tasks': 'Tasks',
            'projectDetail.team': 'Team',
            'projectDetail.lots': 'Lots',
            'projectDetail.documents': 'Documents',
            'projectDetail.stock': 'Stock',
            'projectDetail.alert': 'Alert',
            'projectDetail.studyBet': 'BET Study',
            'projectDetail.pointingAddress': 'Pointing Address',
            'projectDetail.viewQrCode': 'View QR Code',
            'projectDetail.qrCode': 'QR Code',
            'projectDetail.scanMessage': 'Scan with your phone to open the project details.',
            'projectDetail.project': 'Project',
            'projectDetail.status': 'Status',
            'projectDetail.updateDate': 'Update Date',
            'projectDetail.address': 'Address',
            'projectDetail.print': 'Print',
            'projectDetail.download': 'Download',
            'projectDetail.qrUnavailable': 'QR code unavailable',
            'projectDetail.loading': 'Loading...',
            'projectDetail.selectTab': 'Select a tab',
            'projectDetail.attendanceReport': 'Attendance Report',

            // Project Detail - Statuses
            'projectStatus.active': 'Active',
            'projectStatus.inactive': 'Inactive',
            'projectStatus.completed': 'Completed',
            'projectStatus.inProgress': 'In Progress',
            'projectStatus.pending': 'Pending',
            'projectStatus.blocked': 'Blocked',

            // Project Detail - Empty Messages
            'projectDetail.noData': 'No data available',
            'projectDetail.noTasks': 'No tasks',
            'projectDetail.noTeam': 'No team members',
            'projectDetail.noDocuments': 'No documents',
            'projectDetail.noStock': 'No stock',
            'projectDetail.noAlerts': 'No alerts',

            // Project Detail - Confirmations
            'projectDetail.deleteConfirm': 'Are you sure you want to delete?',
            'projectDetail.saveSuccess': 'Saved successfully',
            'projectDetail.saveError': 'Error while saving',

            // Project Detail - Buttons
            'projectDetail.addTask': 'Add Task',
            'projectDetail.addMember': 'Add Member',
            'projectDetail.uploadDocument': 'Upload Document',
            'projectDetail.addStock': 'Add to Stock',
            'projectDetail.reportIssue': 'Report Issue',

            // Dashboard Admin
            'dashboardAdmin.title': 'Administrator Dashboard',
            'dashboardAdmin.users.total': 'Total Users',
            'dashboardAdmin.users.active': 'Active Users',
            'dashboardAdmin.users.pending': 'Pending Validation',
            'dashboardAdmin.projects.total': 'Total Projects',
            'dashboardAdmin.subscriptions.total': 'Total Subscriptions',
            'dashboardAdmin.subscriptions.active': 'Active Subscriptions',
            'dashboardAdmin.subscriptions.expired': 'Expired Subscriptions',
            'dashboardAdmin.revenue.total': 'Cumulative Revenue',
            'dashboardAdmin.revenue.monthly': 'Monthly Revenue',
            'dashboardAdmin.stats': 'Statistics',
            'dashboardAdmin.overview': 'Overview',
            'dashboardAdmin.charts.subscriptions': 'Subscriptions Evolution',
            'dashboardAdmin.charts.revenue': 'Monthly Revenue',
            'dashboardAdmin.charts.profiles': 'Profile Distribution',
            'dashboardAdmin.charts.plans': 'Plan Distribution',
            'dashboardAdmin.invoices.title': 'Latest Paid Invoices',
            'dashboardAdmin.invoices.invoice': 'Invoice',
            'dashboardAdmin.invoices.user': 'User',
            'dashboardAdmin.invoices.plan': 'Plan',
            'dashboardAdmin.invoices.amount': 'Amount',
            'dashboardAdmin.invoices.date': 'Date',
            'dashboardAdmin.invoices.status': 'Status',
            'dashboardAdmin.invoices.paid': 'Paid',
            'dashboardAdmin.invoices.unpaid': 'Unpaid',
            'dashboardAdmin.invoices.noData': 'No invoices available',
            'dashboardAdmin.pagination.showing': 'Showing',
            'dashboardAdmin.pagination.to': 'to',
            'dashboardAdmin.pagination.of': 'of',
            'dashboardAdmin.pagination.results': 'results',
            'dashboardAdmin.pagination.previous': 'Previous',
            'dashboardAdmin.pagination.next': 'Next',

            // Project Presentation
            'projectPresentation.progression': 'Progress',
            'projectPresentation.loadingProgress': 'Loading progress...',
            'projectPresentation.surface': 'Surface',
            'projectPresentation.lots': 'Lots',
            'projectPresentation.startDate': 'Start',
            'projectPresentation.endDate': 'Expected End',
            'projectPresentation.budget': 'Budget',
            'projectPresentation.used': 'Used',
            'projectPresentation.status': 'Status',
            'projectPresentation.updating': 'Updating...',
            'projectPresentation.inProgress': 'In Progress',
            'projectPresentation.paused': 'Paused',
            'projectPresentation.completed': 'Completed',
            'projectPresentation.currentStatus': 'Current Status',
            'projectPresentation.statusChanged': 'Status updated',
            'projectPresentation.noSubcontractor': 'No subcontractor assigned',
            'projectPresentation.generalInfo': 'General Information',
            'projectPresentation.progressState': 'Progress State',
            'projectPresentation.budgetTab': 'Budget',
            'projectPresentation.add': 'Add',
            'projectPresentation.description': 'Description',
            'projectPresentation.amount': 'Amount',
            'projectPresentation.location': 'Location',
            'projectPresentation.numberOfLots': 'Number of Lots',
            'projectPresentation.dueDate': 'Due Date',
            'projectPresentation.numberOfRooms': 'Number of Rooms',
            'projectPresentation.commonEquipment': 'Common Equipment',
            'projectPresentation.budgetInfo': 'Budget Information',

            // ============ TEAM COMPONENT ============
            'team.searchPlaceholder': 'Search here...',
            'team.filterByRole': 'Filter by role',
            'team.allRoles': 'All roles',
            'team.newMember': 'New member',
            'team.name': 'Name',
            'team.role': 'Role',
            'team.phone': 'Phone',
            'team.present': 'Present',
            'team.action': 'Action',
            'team.loading': 'Loading...',
            'team.retry': 'Retry',
            'team.yes': 'Yes',
            'team.no': 'No',
            'team.noMembers': 'No team members found',
            'team.startAddingMembers': 'Start by adding members to your team',
            'team.previous': 'Previous',
            'team.next': 'Next',
            'team.addNewMember': 'Add a new member',
            'team.lastName': 'Last Name',
            'team.firstName': 'First Name',
            'team.email': 'Email',
            'team.birthDate': 'Birth Date',
            'team.birthPlace': 'Birth Place',
            'team.address': 'Address',
            'team.profilePhoto': 'Profile Photo',
            'team.password': 'Password',
            'team.cancel': 'Cancel',
            'team.add': 'Add',
            'team.adding': 'Adding...',
            'team.details': 'Details',
            'team.daysPresent': 'Days Present',
            'team.hoursWorked': 'Hours Worked',
            'team.tasksCompleted': 'Tasks Completed',
            'team.performance': 'Performance',
            'team.mon': 'Mon',
            'team.tue': 'Tue',
            'team.wed': 'Wed',
            'team.thu': 'Thu',
            'team.fri': 'Fri',
            'team.sat': 'Sat',
            'team.sun': 'Sun',
            'team.taskDistribution': 'Task Distribution',
            'team.toDo': 'To Do',
            'team.inProgress': 'In Progress',
            'team.completed': 'Completed',
            'team.late': 'Late',

            // ============ TASK BOARD / TASKS ============
            'tasks.todo': 'To Do',
            'tasks.inProgress': 'In Progress',
            'tasks.blocked': 'Blocked',
            'tasks.done': 'Done',
            'team.presenceHistory': 'Presence History',
            'team.filterByDate': 'Filter by date',
            'team.entry': 'Entry',
            'team.exit': 'Exit',
            'team.total': 'Total',
            'team.noPresenceHistory': 'No presence history available',
            'team.totalHoursWorked': 'Total Hours Worked',
            'team.close': 'Close',
            'team.worker': 'Employee',
            'team.acceptedFormats': 'Accepted formats: JPG, PNG, GIF (Max 5MB)',
            'team.passwordMinLength': 'Password (minimum 6 characters)',
            'team.profile': 'Profile',
            'team.profiles.worker': 'Employee',
            'team.profiles.siteManager': 'Site Manager',
            'team.profiles.subcontractor': 'Subcontractor',
            'team.profiles.promoteur': 'Promoter',
            'team.editMember': 'Edit Employee',
            'team.save': 'Save',
            'team.saving': 'Saving...',
            'team.loadingData': 'Loading information...',
            'team.phoneReadOnly': 'Login identifier — read only',
            'team.deleteTitle': 'Delete employee?',
            'team.deleteConfirmBefore': 'Are you sure you want to delete',
            'team.deleteConfirmAfter': '? This action is irreversible.',
            'team.delete': 'Delete',
            'team.deleting': 'Deleting...',
            'team.deleteSuccess': 'Employee deleted',
            'team.deleteSuccessMsg': 'has been removed from your team.',
            'team.deleteError': 'Deletion failed. Please try again.',
            'team.deleteConstraintError': 'This employee cannot be deleted because they have linked data (leave requests, attendance…).',
            'team.deactivateInstead': 'Deactivate account instead',
            'team.deactivating': 'Deactivating...',
            'team.deactivateSuccess': 'Account deactivated',
            'team.deactivateSuccessMsg': 'has been deactivated and will no longer have access to the application.',
            'common.paid': 'Payé',
            'common.unpaid': 'Non payé',

            // ============ DOCUMENTS COMPONENT ============
            'documents.title': 'Documents',
            'documents.search': 'Search for a document...',
            'documents.allTypes': 'All types',
            'documents.addDocument': 'Add document',
            'documents.name': 'Name',
            'documents.type': 'Type',
            'documents.size': 'Size',
            'documents.date': 'Date',
            'documents.actions': 'Actions',
            'documents.download': 'Download',
            'documents.delete': 'Delete',
            'documents.view': 'View',
            'documents.noDocuments': 'No documents found',
            'documents.uploadDocument': 'Upload a document',
            'documents.dragDrop': 'Drag and drop your files here',
            'documents.or': 'or',
            'documents.recentActivities': 'Recent Activities',
            'documents.newDocument': 'New Document',
            'documents.lastModified': 'Last Modified',
            'documents.fileSize': 'File Size',
            'documents.allStatus': 'All Status',
            'documents.browse': 'Browse',
            'documents.cancel': 'Cancel',
            'documents.upload': 'Upload',
            'documents.uploading': 'Uploading...',
            'documents.deleteConfirm': 'Are you sure you want to delete this document?',
            'documents.confirmDelete': 'Confirm deletion',
            'documents.category': 'Category',
            'documents.allCategories': 'All categories',
            'documents.administrative': 'Administrative',
            'documents.technical': 'Technical',
            'documents.financial': 'Financial',
            'documents.other': 'Other',

            // ============ LOTS COMPONENT ============
            'lots.title': 'Lots',
            'lots.newLot': 'New lot',
            'lots.search': 'Search for a lot...',
            'lots.addLot': 'Add lot',
            'lots.lotNumber': 'Lot number',
            'lots.lotName': 'Lot name',
            'lots.description': 'Description',
            'lots.subcontractor': 'Subcontractor',
            'lots.budget': 'Budget',
            'lots.progress': 'Progress',
            'lots.status': 'Status',
            'lots.actions': 'Actions',
            'lots.notStarted': 'Not started',
            'lots.inProgress': 'In progress',
            'lots.completed': 'Completed',
            'lots.suspended': 'Suspended',
            'lots.noLots': 'No lots found',
            'lots.addNewLot': 'Add a new lot',
            'lots.editLot': 'Edit lot',
            'lots.cancel': 'Cancel',
            'lots.save': 'Save',
            'lots.saving': 'Saving...',
            'lots.deleteConfirm': 'Are you sure you want to delete this lot?',
            'lots.confirmDelete': 'Confirm deletion',
            'lots.assignSubcontractor': 'Assign subcontractor',
            'lots.selectSubcontractor': 'Select a subcontractor',
            'lots.noSubcontractor': 'No subcontractor assigned',
            'lots.startDate': 'Start date',
            'lots.endDate': 'End date',
            'lots.estimatedBudget': 'Estimated budget',
            'lots.actualBudget': 'Actual budget',
            'lots.view': 'View',
            'lots.edit': 'Edit',
            'lots.delete': 'Delete',

            // ============ ALERT/SIGNALEMENT COMPONENT ============
            'alert.title': 'Alerts',
            'alert.search': 'Search for an alert...',
            'alert.addAlert': 'New alert',
            'alert.alertType': 'Alert type',
            'alert.priority': 'Priority',
            'alert.status': 'Status',
            'alert.date': 'Date',
            'alert.reportedBy': 'Reported by',
            'alert.description': 'Description',
            'alert.actions': 'Actions',
            'alert.high': 'High',
            'alert.medium': 'Medium',
            'alert.low': 'Low',
            'alert.open': 'Open',
            'alert.inProgress': 'In progress',
            'alert.resolved': 'Resolved',
            'alert.closed': 'Closed',
            'alert.noAlerts': 'No alerts',
            'alert.addNewAlert': 'Add an alert',
            'alert.editAlert': 'Edit alert',
            'alert.cancel': 'Cancel',
            'alert.save': 'Save',
            'alert.saving': 'Saving...',
            'alert.deleteConfirm': 'Are you sure you want to delete this alert?',
            'alert.confirmDelete': 'Confirm deletion',
            'alert.safety': 'Safety',
            'alert.quality': 'Quality',
            'alert.delay': 'Delay',
            'alert.material': 'Material',
            'alert.other': 'Other',
            'alert.location': 'Location',
            'alert.photos': 'Photos',
            'alert.addPhotos': 'Add photos',
            'alert.view': 'View',
            'alert.edit': 'Edit',
            'alert.delete': 'Delete',
            'alert.resolve': 'Resolve',
            'alert.reopen': 'Reopen',
            'alert.allTypes': 'All types',
            'alert.allPriorities': 'All priorities',
            'alert.allStatuses': 'All statuses',

            // ============ STUDY BET COMPONENT ============
            'studyBet.title': 'Technical Studies',
            'studyBet.search': 'Search for a study...',
            'studyBet.addStudy': 'New study',
            'studyBet.studyName': 'Study name',
            'studyBet.studyType': 'Study type',
            'studyBet.table.betName': 'Bet name',
            'studyBet.table.title': 'Title',
            'studyBet.table.description': 'Description',
            'studyBet.table.creationDate': 'Creation date',
            'studyBet.table.status': 'Status',
            'studyBet.table.actions': 'Actions',
            'studyBet.office': 'Technical office',
            'studyBet.pagination.showing': 'Showing',
            'studyBet.pagination.to': 'to',
            'studyBet.pagination.of': 'of',
            'studyBet.pagination.results': 'results',
            'studyBet.status': 'Status',
            'studyBet.pagination.previous': 'Previous',
            'studyBet.pagination.next': 'Next',
            'studyBet.newStudy': 'New study',
            'studyBet.deliveryDate': 'Delivery date',
            'studyBet.actions': 'Actions',
            'studyBet.pending': 'Pending',
            'studyBet.inProgress': 'In progress',
            'studyBet.delivered': 'Delivered',
            'studyBet.validated': 'Validated',
            'studyBet.rejected': 'Rejected',
            'studyBet.noStudies': 'No studies found',
            'studyBet.addNewStudy': 'Add a study',
            'studyBet.editStudy': 'Edit study',
            'studyBet.cancel': 'Cancel',
            'studyBet.save': 'Save',
            'studyBet.saving': 'Saving...',
            'studyBet.deleteConfirm': 'Are you sure you want to delete this study?',
            'studyBet.confirmDelete': 'Confirm deletion',
            'studyBet.structural': 'Structural',
            'studyBet.electrical': 'Electrical',
            'studyBet.plumbing': 'Plumbing',
            'studyBet.hvac': 'HVAC',
            'studyBet.architectural': 'Architectural',
            'studyBet.geotechnical': 'Geotechnical',
            'studyBet.other': 'Other',
            'studyBet.documents': 'Documents',
            'studyBet.comments': 'Comments',
            'studyBet.version': 'Version',
            'studyBet.view': 'View',
            'studyBet.edit': 'Edit',
            'studyBet.delete': 'Delete',
            'studyBet.validate': 'Validate',
            'studyBet.reject': 'Reject',
            'studyBet.download': 'Download',
            'studyBet.listTitle': 'Study Requests List',
            'studyBet.loading': 'Loading...',
            'studyBet.titleLabel': 'Title',
            'studyBet.description': 'Description',
            'studyBet.betName': 'BET Name',
            'studyBet.creationDate': 'Creation Date',
            'studyBet.viewDetails': 'View Details',
            'studyBet.previous': 'Previous',
            'studyBet.next': 'Next',
            'studyBet.pagination': 'Showing {{start}} to {{end}} of {{total}} results',
            'studyBet.selectBet': 'Select a BET',
            'studyBet.placeholder': 'Enter value',
            'studyBet.send': 'Send',
            'studyBet.info': 'Information',
            'studyBet.concernedBet': 'Concerned BET',
            'studyBet.state': 'State',
            'studyBet.associatedReports': 'Associated Reports',
            'studyBet.addReport': 'Add Report',
            'studyBet.submittedOn': 'Submitted on',
            'studyBet.noReports': 'No reports',
            'studyBet.noComments': 'No comments',
            'studyBet.close': 'Close',
            'studyBet.validateConfirmTitle': 'Validate Request?',
            'studyBet.validateConfirmMessage': 'This request will be marked as Validated',
            'studyBet.rejectConfirmTitle': 'Reject Request?',
            'studyBet.rejectConfirmMessage': 'This request will be marked as Rejected',
            'studyBet.addReason': 'You can add a reason',
            'studyBet.reasonPlaceholder': 'Reason for rejection...',
            'studyBet.newReport': 'New Report',
            'studyBet.document': 'Document',
            'studyBet.chooseFile': 'Choose a file',
            'studyBet.formats': 'Accepted formats: PDF, Word, Excel',
            'studyBet.sending': 'Sending...',
            'studyBet.step1': 'Pending Response',
            'studyBet.step2': 'Acceptance in Progress',
            'studyBet.step3': 'Delivery in Progress',
            'studyBet.step4': 'Validation/Rejection',
            'studyBet.reportTitle': 'Title',
            'studyBet.deleteReportConfirm': 'Are you sure you want to delete report "{name}"?',
            'studyBet.deleteReportSuccess': 'Report deleted successfully',
            'studyBet.reportDeleteError': 'Error deleting report',
            'studyBet.validateSuccess': 'Study validated successfully',
            'studyBet.rejectSuccess': 'Study rejected successfully',
            'studyBet.commentEmpty': 'Comment content empty',
            'studyBet.noStudySelected': 'No study selected',
            'studyBet.userNotConnected': 'User not connected',
            'studyBet.userIdMissing': 'User ID missing',
            'studyBet.commentAdded': 'Comment added successfully',
            'studyBet.commentError': 'Error adding comment',
            'studyBet.sessionExpired': 'Session expired. Please login again.',
            'studyBet.noPermission': 'You do not have permission to add a comment.',
            'studyBet.studyNotFound': 'Study no longer exists.',
            'studyBet.genericCommentError': 'Error adding comment. Please try again.',
            'studyBet.reportCreated': 'Report created successfully',
            'studyBet.reportUpdate': 'Report updated successfully',
            'studyBet.createError': 'Error creating study',
            'studyBet.updateError': 'Error updating study',
            'studyBet.reportUpdateError': 'Error updating report',
            'studyBet.validateError': 'Error validating study',
            'studyBet.rejectError': 'Error rejecting study',
            'studyBet.searchPlaceholder': 'Search...',
            'studyBet.downloadError': 'Download URL not available for: {name}',

            // ============ POINTING ADDRESS COMPONENT ============
            'pointingAddress.title': 'Check-in Address',
            'pointingAddress.search': 'Search...',
            'pointingAddress.addAddress': 'Add address',
            'pointingAddress.addressName': 'Address name',
            'pointingAddress.fullAddress': 'Full address',
            'pointingAddress.latitude': 'Latitude',
            'pointingAddress.longitude': 'Longitude',
            'pointingAddress.radius': 'Radius (meters)',
            'pointingAddress.status': 'Status',
            'pointingAddress.actions': 'Actions',
            'pointingAddress.active': 'Active',
            'pointingAddress.inactive': 'Inactive',
            'pointingAddress.noAddresses': 'No check-in addresses',
            'pointingAddress.addNewAddress': 'Add a check-in address',
            'pointingAddress.editAddress': 'Edit address',
            'pointingAddress.cancel': 'Cancel',
            'pointingAddress.save': 'Save',
            'pointingAddress.saving': 'Saving...',
            'pointingAddress.deleteConfirm': 'Are you sure you want to delete this address?',
            'pointingAddress.confirmDelete': 'Confirm deletion',
            'pointingAddress.selectOnMap': 'Select on map',
            'pointingAddress.currentLocation': 'Current location',
            'pointingAddress.view': 'View',
            'pointingAddress.edit': 'Edit',
            'pointingAddress.delete': 'Delete',
            'pointingAddress.activate': 'Activate',
            'pointingAddress.deactivate': 'Deactivate',

            // ============ STATUS REPORT/ALBUMS COMPONENT ============
            'statusReport.albumsView': 'Albums View',
            'statusReport.graphView': 'Graph View',
            'statusReport.indicatorsTable': 'Indicators Table',
            'statusReport.viewPhotos': 'View photos',
            'statusReport.editAlbum': 'Edit album',
            'statusReport.deleteAlbum': 'Delete album',
            'statusReport.photos': 'photos',
            'statusReport.step': 'Step',
            'statusReport.percentage': '%Percentage',
            'statusReport.updateDate': 'Update Date',
            'statusReport.selectPercentage': 'Select a percentage',
            'statusReport.close': 'Close',
            'statusReport.createAlbum': 'Create a new album',
            'statusReport.albumName': 'Album name',
            'statusReport.description': 'Description',
            'statusReport.albumPhotos': 'Album photos',
            'statusReport.clickToAdd': 'Click to add photos or drag and drop',
            'statusReport.acceptedFormats': 'PNG, JPG, JPEG up to 10MB',
            'statusReport.selectedPhotos': 'Selected photos',
            'statusReport.cancel': 'Cancel',
            'statusReport.create': 'Create',
            'statusReport.modify': 'Modify',
            'statusReport.currentPhotos': 'Current photos',
            'statusReport.addNewPhotos': 'Add new photos',
            'statusReport.confirmDeletion': 'Confirm deletion',
            'statusReport.deleteConfirmMessage': 'Are you sure you want to delete the album',
            'statusReport.irreversibleAction': 'This action is irreversible.',
            'statusReport.noCancel': 'No, cancel',
            'statusReport.yesDelete': 'Yes, delete',
            'statusReport.noPhotos': 'No photos in this album',
            'statusReport.projectNotFound': 'Project not found',
            'statusReport.accessDeniedLoad': 'Access denied while loading {type}. Check your permissions.',
            'statusReport.sessionExpired': 'Session expired. Please login again.',
            'statusReport.loadError': 'Error while loading {type}',
            'statusReport.noAlbumSelected': 'No album selected for modification',
            'statusReport.albumModifiedSuccess': 'Album modified successfully',
            'statusReport.albumGenericError': 'Error modifying album',
            'statusReport.indicatorNotFound': 'Indicator not found. Please reload the page.',
            'statusReport.albumNameRequired': 'Album name is required',
            'statusReport.projectIdInvalid': 'Invalid Project ID',
            'statusReport.invalidPictureFormat': 'All images must be valid base64 or URL',
            'statusReport.fileTooLarge': 'File {name} exceeds maximum size of 5MB',
            'statusReport.invalidImageType': 'File {name} is not a valid image',
            'statusReport.conversionError': 'Error converting {name}',
            'statusReport.readError': 'Error reading file {name}',
            'statusReport.albumCreatedSuccess': 'Album created successfully',
            'statusReport.albumDeletedSuccess': 'Album deleted successfully',
            'statusReport.noPermission': 'You do not have rights for this {operation}.',
            'statusReport.invalidData': 'Invalid data. Check your input.',
            'statusReport.albumNotFound': 'Album not found.',
            'statusReport.genericOperationError': 'Error during album {operation}. Please try again.',
            'statusReport.updateSuccess': '{phaseName} updated to {progress}%',
            'statusReport.updateError': 'Error updating {phaseName}',
            'statusReport.accessDeniedUpdate': 'You do not have rights to update {phaseName}.',

            // Logout Confirmation
            'logout.confirmTitle': 'Confirm Logout',
            'logout.confirmMessage': 'Are you sure you want to log out?',
            'logout.confirm': 'Log Out',
            // Equipment
            'equipment.hall': 'Entrance Hall',
            'equipment.hallDesc': 'Building reception area',
            'equipment.elevator': 'Elevator',
            'equipment.elevatorDesc': 'Access to different levels',
            'equipment.parking': 'Parking',
            'equipment.parkingDesc': 'Parking spaces',
            'equipment.pool': 'Swimming Pool',
            'equipment.poolDesc': 'Swimming pool',
            'equipment.gym': 'Gym',
            'equipment.gymDesc': 'Equipped fitness area',
            'equipment.playground': 'Playground',
            'equipment.playgroundDesc': 'Children\'s play area',
            'equipment.security': 'Security Service',
            'equipment.securityDesc': '24/7 Surveillance',
            'equipment.garden': 'Garden',
            'equipment.gardenDesc': 'Landscaped green space',
            'equipment.terrace': 'Shared Terrace',
            'equipment.terraceDesc': 'Common outdoor space',
            'equipment.bikeStorage': 'Bicycle Storage',
            'equipment.bikeStorageDesc': 'Secure bicycle parking',
            'equipment.laundry': 'Laundry Room',
            'equipment.laundryDesc': 'Common laundry area',
            'equipment.storage': 'Storage Rooms',
            'equipment.storageDesc': 'Individual storage spaces',
            'equipment.corridors': 'Corridors',
            'equipment.corridorsDesc': 'Circulation spaces',
            'common.completed': 'completed',

            // ============ PROJECT BUDGET COMPONENT ============
            'projectBudget.title': 'Budget Management',
            'projectBudget.editBudget': 'Edit Budget',
            'projectBudget.loading': 'Loading...',
            'projectBudget.plannedBudget': 'Planned Budget',
            'projectBudget.remainingBudget': 'Remaining Budget',
            'projectBudget.consumedBudget': 'Consumed Budget',
            'projectBudget.expensesList': 'Expenses List',
            'projectBudget.newExpense': 'New Expense',
            'projectBudget.date': 'Date',
            'projectBudget.description': 'Description',
            'projectBudget.evidence': 'Evidence',
            'projectBudget.amount': 'Amount',
            'projectBudget.actions': 'Actions',
            'projectBudget.noExpenses': 'No expenses',
            'projectBudget.startAdding': 'Start by adding a new expense.',
            'projectBudget.previous': 'Previous',
            'projectBudget.next': 'Next',
            'projectBudget.editBudgetModal': 'Edit Budget',
            'projectBudget.budgetAmount': 'Budget Amount',
            'projectBudget.cancel': 'Cancel',
            'projectBudget.save': 'Save',
            'projectBudget.saving': 'Saving...',
            'projectBudget.editExpenseModal': 'Edit Expense',
            'projectBudget.newExpenseModal': 'New Expense',
            'projectBudget.expenseDescription': 'Expense Description',
            'projectBudget.amountInCurrency': 'Amount',
            'projectBudget.evidenceOptional': 'Evidence (optional)',
            'projectBudget.clickToBrowse': 'Click to browse or drag a file',
            'projectBudget.acceptedFormats': 'PDF, JPG, PNG (max 5MB)',
            'projectBudget.confirmDelete': 'Confirm Deletion',
            'projectBudget.deleteConfirmMessage': 'Are you sure you want to delete this expense?',
            'projectBudget.irreversibleAction': 'This action is irreversible.',
            'projectBudget.delete': 'Delete',
            'projectBudget.deleting': 'Deleting...',
            'projectBudget.evidenceModal': 'Expense Evidence',
            'projectBudget.noEvidence': 'No evidence available',
            'projectBudget.downloadEvidence': 'Download Evidence',
            'projectBudget.close': 'Close',
            'projectBudget.fileTooBig': 'File must not exceed 5MB',
            'projectBudget.invalidFormat': 'Unsupported format. Use PDF, JPG or PNG',
            // ============ NEW PROJECT COMPONENT ============
            'newProject.title': 'New Project',
            'newProject.cancelConfirm': 'Cancel Confirmation',
            'newProject.cancelConfirmMessage': 'Are you sure you want to cancel?',
            'newProject.subtitle': 'Fill this form to create a new project',
            'newProject.assignedManager': 'Assigned Manager',
            'newProject.loading': 'Loading data...',
            'newProject.retry': 'Retry',
            'newProject.generalInfo': 'General Information',
            'newProject.projectType': 'Project Type',
            'newProject.selectType': 'Select a type',
            'newProject.projectName': 'Project Name',
            'newProject.number': 'Number',
            'newProject.area': 'Area (m²)',
            'newProject.price': 'Total Price (F CFA)',
            'newProject.startDate': 'Expected Start Date',
            'newProject.endDate': 'Expected End Date',
            'newProject.address': 'Address',
            'newProject.numberOfLots': 'Number of Lots',
            'newProject.numberOfRooms': 'Number of Rooms',
            'newProject.constructionStatus': 'Construction Status',
            'newProject.selectStatus': 'Select status',
            'newProject.latitude': 'Latitude (optional)',
            'newProject.longitude': 'Longitude (optional)',
            'newProject.description': 'Description',
            'newProject.commonEquipment': 'Common Equipment',
            'newProject.documents': 'Attached Documents',
            'newProject.plan': 'Plan (image)',
            'newProject.clickToBrowse': 'Click to browse',
            'newProject.remove': 'Remove',
            'newProject.save': 'Save',
            'newProject.saving': 'Saving...',
            'newProject.cancel': 'Cancel',
            'newProject.errors': 'Please correct the following errors:',
            'newProject.required': 'This field is required',
            'newProject.minLength': 'Minimum characters required',
            'newProject.maxLength': 'Maximum characters exceeded',
            'newProject.min': 'Value too low',
            'newProject.dateRange': 'End date must be after start date',
            'newProject.planRequired': 'The plan (image) is required',
            'newProject.success': 'Project created successfully!',
            'newProject.accessDenied': 'Access denied. Please login.',

            // ============ TASK BOARD / TASKS (Complement) ============
            'taskBoard.title': 'Task Board',
            'taskBoard.searchPlaceholder': 'Search here...',
            'taskBoard.newTask': 'New Task',
            'taskBoard.editTask': 'Edit Task',
            'taskBoard.column.TODO': 'To Do',
            'taskBoard.column.IN_PROGRESS': 'In Progress',
            'taskBoard.column.BLOCKED': 'Blocked',
            'taskBoard.column.DONE': 'Done',
            'taskBoard.task.comments': 'Comments',
            'taskBoard.task.attachments': 'Attachments',
            'taskBoard.task.dueDate': 'Due Date',
            'taskBoard.task.noTasks': 'No tasks',
            'taskBoard.task.clickToAdd': 'Click + to add a task',
            'taskBoard.task.scrollMore': 'Scroll to see more',
            'taskBoard.form.title': 'Title',
            'taskBoard.form.description': 'Description',
            'taskBoard.form.priority': 'Priority',
            'taskBoard.form.startDate': 'Start Date',
            'taskBoard.form.endDate': 'End Date',
            'taskBoard.form.status': 'Status',
            'taskBoard.form.assignUsers': 'Assign to employees',
            'taskBoard.form.selectWorkers': 'Selected Employees',
            'taskBoard.form.noWorkerAvailable': 'No employee available',
            'taskBoard.form.clickToAssign': 'Click on an employee to assign',
            'taskBoard.form.images': 'Images',
            'taskBoard.form.clickToAddImages': 'Click to add images',
            'taskBoard.form.imageFormat': 'PNG, JPG up to 10MB',
            'taskBoard.form.selectedFiles': 'selected file(s)',
            'taskBoard.modal.joinFileTitle': 'Attach a file',
            'taskBoard.modal.fileLabel': 'File Label',
            'taskBoard.modal.selectFile': 'Select a file',
            'taskBoard.modal.clickToSelect': 'Click to select',
            'taskBoard.modal.dragDrop': 'or drag and drop a file',
            'taskBoard.modal.joinFileButton': 'Attach file',
            'taskBoard.detail.gallery': 'Image Gallery',
            'taskBoard.detail.viewBig': 'View full size',
            'taskBoard.detail.period': 'Period',
            'taskBoard.detail.start': 'Start',
            'taskBoard.detail.end': 'End',
            'taskBoard.detail.stats': 'Statistics',
            'taskBoard.detail.assignedTeam': 'Assigned Team',
            'taskBoard.detail.executor': 'Executor',
            'taskBoard.detail.noExecutor': 'No executor assigned',
            'taskBoard.detail.clickEditToAssign': 'Click "Edit" to assign members',
            'taskBoard.priority.LOW': 'Low',
            'taskBoard.priority.MEDIUM': 'Medium',
            'taskBoard.priority.HIGH': 'High',
            'taskBoard.modal.attachedFiles': 'Currently attached files',
            'taskBoard.modal.addNewFile': 'Add a new file',
            'taskBoard.modal.selectedFile': 'Selected file',
            'taskBoard.modal.joining': 'Joining in progress...',
            'taskBoard.task.members': 'member(s)',

            // ============ STOCK COMPONENT ============
            'stock.title': 'Stock',
            // Tabs
            'stock.tabs.inventories': 'Inventories',
            'stock.tabs.orders': 'Orders',
            'stock.tabs.deliveries': 'Deliveries',
            'stock.tabs.statistics': 'Statistics',
            'stock.stats.materialsUsed': 'Materials Used',
            'stock.stats.ordersPlaced': 'Orders Placed',
            'stock.stats.deliveriesReceived': 'Deliveries Received',
            'stock.stats.alertsTriggered': 'Alerts Triggered',
            'stock.stats.categoryDistribution': 'Category Distribution',
            'stock.stats.lowStockAlerts': 'Low Stock Alerts',
            'stock.stats.materialsByUnit': 'Materials by Unit',
            'stock.stats.totalCategories': 'Total Categories',
            'stock.stats.totalMaterials': 'Total Materials',
            'stock.stats.totalOrders': 'Total Orders',
            'stock.stats.totalDeliveries': 'Total Deliveries',
            'stock.stats.totalAlerts': 'Total Alerts',
            'stock.orders.orderNumber': 'Order Number',
            'stock.orders.supplier': 'Supplier',
            'stock.orders.orderDate': 'Order Date',
            'stock.orders.deliveryDate': 'Delivery Date',
            'stock.orders.status': 'Status',
            'stock.orders.actions': 'Actions',
            'stock.table.status': 'Status',
            'stock.table.status.PENDING': 'Pending',
            'stock.table.status.DELIVERED': 'Delivered',
            'stock.table.status.CANCELLED': 'Cancelled',
            'stock.deliveries.proof': 'Proof',
            'stock.deliveries.noProof': 'No proof',
            'stock.deliveries.deliveryNumber': 'Delivery Number',
            'stock.deliveries.deliveryDate': 'Delivery Date',
            'stock.deliveries.supplier': 'Supplier',
            'stock.deliveries.status': 'Status',
            'stock.deliveries.actions': 'Actions',
            'stock.deliveries.allDeliveries': 'All Deliveries',
            'stock.deliveries.pendingDeliveries': 'Pending Deliveries',
            'stock.deliveries.deliveredDeliveries': 'Delivered Deliveries',
            'stock.deliveries.cancelledDeliveries': 'Cancelled Deliveries',
            'stock.deliveries.partiallyDeliveredDeliveries': 'Partially Delivered Deliveries',
            'stock.status.complete': 'Completed',
            'stock.status.partial': 'Partial',
            'stock.status.cancelled': 'Cancelled',
            'stock.pagination.orders': 'Orders',
            'stock.pagination.deliveries': 'Deliveries',
            'stock.pagination.materials': 'Materials',
            'stock.pagination.total': 'Total',
            'stock.pagination.page': 'Page',
            'stock.pagination.of': 'of',
            'stock.pagination.showing': 'Showing',
            'stock.filters.searchPlaceholder': 'Search...',
            'stock.orders.searchPlaceholder': 'Search...',
            'stock.orders.allStatuses': 'All Statuses',
            'stock.status.pending': 'Pending',
            'stock.orderDate': 'Order Date',
            'stock.phone': 'Phone',
            'stock.quotes': 'Quotes',
            'stock.quote': 'Quote',
            'stock.selected': 'Selected',
            'stock.supplierName': 'Supplier',
            'stock.uploadDate': 'Upload Date',
            'stock.noQuotes': 'No quotes available',
            'stock.noQuotesDesc': 'Suppliers have not yet sent quotes',
            'stock.orderedMaterials': 'Ordered Materials',
            'stock.approveOrder': 'Approve Order',
            'stock.approveOrderTitle': 'Approve this order?',
            'stock.approveOrderMessage': 'You are about to approve the order',
            'stock.totalAmount': 'Total Amount',
            'stock.approveOrderWarning': 'This action will validate the quote and allow order processing.',
            'stock.approving': 'Approving...',
            'stock.confirmApproval': 'Confirm Approval',
            'stock.downloadInvoice': 'Download Invoice',
            // ============ DEMANDES COMPONENT ============
            'demande.title': 'Requests',
            'documents.titleLabel': 'Title',
            'documents.typeLabel': 'Type',
            'documents.fileLabel': 'File',
            'documents.descriptionLabel': 'Description',
            'documents.clickToSelect': 'Click to select',
            'documents.startDate': 'Start Date',
            'documents.endDate': 'End Date',
            'documents.chooseType': 'Choose Type',
            'documents.statusLabel': 'Status',
            'documents.actionsLabel': 'Actions',
            'documents.fileFormats': 'File Formats',
            'demande.subtitle': 'Track and manage your requests to Technical Offices (BET)',
            'demande.searchPlaceholder': 'Search by title, description...',
            'demande.period': 'Period',
            'demande.details.subject': 'Subject',
            'demande.details.description': 'Description',
            'demande.details.bet': 'BET',
            'demande.details.createdAt': 'Date de création',
            'demande.details.status': 'Statut',
            'demande.details.actions': 'Actions',
            'demande.details.reports': 'Rapports',
            'demande.steps.pendingDetails': 'Pending Details',
            'demande.steps.inProgressDetails': 'In Progress Details',
            'demande.steps.delivered': 'Delivered',
            'demande.steps.validatedDetails': 'Validated Details',
            'demande.steps.rejectedDetails': 'Rejected Details',
            'demande.details.addCommentPlaceholder': 'Ajouter un commentaire',
            'demande.period.week': 'This Week',
            'demande.period.month': 'This Month',
            'demande.period.quarter': 'This Quarter',
            'demande.status': 'Status',
            'demande.pagination.showing': 'Showing',
            'demande.pagination.of': 'of',
            'demande.pagination.total': 'Total',
            'demande.pagination.page': 'Page',
            'demande.pagination.to': 'to',
            'demande.pagination.from': 'from',
            'demande.pagination.results': 'results',
            'settings.unitOfMeasure': 'Unit of Measure',
            'settings.documents': 'Documents',
            'settings.propertyType': 'Property Type',
            'settings.materialCategories': 'Material Categories',
            'demande.status.PENDING': 'Pending',
            'demande.status.IN_PROGRESS': 'In Progress',
            'demande.status.DELIVERED': 'Delivered',
            'demande.status.VALIDATED': 'Validated',
            'demande.status.REJECTED': 'Rejected',
            'demande.table.title': 'Title',
            'demande.table.description': 'Description',
            'demande.table.bet': 'BET',
            'demande.table.createdAt': 'Created At',
            'demande.table.status': 'Status',
            'demande.table.actions': 'Actions',
            'demande.empty': 'No requests found',
            'demande.newRequest': 'New Request',
            'demande.details.title': 'Request Details',
            'demande.details.downloadReport': 'Download Report',
            'demande.details.noReports': 'No reports available',
            'demande.details.comments': 'Comments',
            'demande.details.noComments': 'No comments yet',
            'demande.details.send': 'Send',
            'common.send': 'Send',
            'common.validate': 'Validate',
            'demande.actions.viewDetails': 'View Details',
            'demande.details.writeComment': 'Write a comment...',
            'demande.actions.validate': 'Validate',
            'demande.actions.reject': 'Reject',
            'demande.actions.delete': 'Delete',
            'demande.modals.report.title': 'New Report',
            'demande.modals.report.subjectLabel': 'Subject',
            'demande.modals.report.versionLabel': 'Version',
            'demande.modals.report.documentLabel': 'Document',
            'demande.modals.report.versionPlaceholder': 'Version',
            'demande.modals.report.subjectPlaceholder': 'Subject',
            'demande.modals.report.documentPlaceholder': 'Document',
            'common.chooseFile': 'Choose File',
            'demande.details.addReport': 'Add Report',
            'demande.actions.createReport': 'Create Report',
            'demande.modals.validate.title': 'Validate Request',
            'demande.modals.validate.message': 'Are you sure you want to validate this request?',
            'demande.modals.reject.title': 'Reject Request',
            'demande.modals.reject.message': 'Are you sure you want to reject this request?',
            'demande.modals.reject.reason': 'Reason for rejection',
            'demande.modals.reject.reasonPlaceholder': 'Enter the reason...',
            'demande.modals.delete.title': 'Delete Request',
            'demande.modals.delete.message': 'Are you sure you want to delete this request?',
            'demande.modals.createReport.title': 'New Report',
            'demande.modals.createReport.reportTitle': 'Report Title',
            'demande.modals.createReport.version': 'Version',
            'demande.modals.createReport.file': 'File',
            'demande.steps.pending': 'Pending Response',
            'demande.steps.inProgress': 'Acceptance in Progress',
            'demande.steps.delivery': 'Delivery in Progress',
            'demande.steps.validation': 'Validation/Rejection',
            'demande.success.validated': 'Request validated successfully',
            'demande.success.rejected': 'Request rejected successfully',
            'demande.success.deleted': 'Request deleted successfully',
            'demande.success.commentAdded': 'Comment added successfully',
            'demande.success.reportCreated': 'Report created successfully',
            'demande.error.load': 'Error loading requests',
            'demande.error.action': 'Error performing action',
            'demande.error.fileRequired': 'Please select a file',
            'demande.error.fieldsRequired': 'Please fill all required fields',
            // Aliases for DemandeComponent
            'demande.header.title': 'Requests',
            'demande.header.description': 'Track and manage your requests to Technical Offices (BET)',
            'demande.filters.searchPlaceholder': 'Search by title, description...',
            'demande.filters.period': 'Period',
            'demande.filters.thisWeek': 'This Week',
            'demande.filters.thisMonth': 'This Month',
            'demande.filters.thisQuarter': 'This Quarter',
            'demande.filters.status': 'Status',
            'demande.loading': 'Loading requests...',
            'demande.empty.title': 'No requests',
            'demande.empty.message': 'No requests found at the moment.',

            // ============ ÉTUDE BET COMPONENT ============
            // Modal: New study
            'studyBet.modal.newStudy': 'New Study',
            'studyBet.modal.editStudy': 'Edit Study',
            'studyBet.modal.newReport': 'New Report',
            'studyBet.modal.selectBET': 'Select BET',
            'studyBet.modal.selectBETPlaceholder': 'Select a BET',
            'studyBet.modal.title': 'Title',
            'studyBet.modal.titleLabel': 'Title',
            'studyBet.modal.description': 'Description',
            'studyBet.modal.descriptionLabel': 'Description',
            'studyBet.modal.version': 'Version',
            'studyBet.modal.versionLabel': 'Version',
            'studyBet.modal.document': 'Document',
            'studyBet.modal.documentLabel': 'Document',
            'studyBet.modal.enterPlaceholder': 'Enter',
            'studyBet.modal.loadingBETs': 'Loading BETs...',
            'studyBet.modal.sending': 'Sending...',
            'studyBet.modal.acceptedFormats': 'Accepted formats: PDF, Word, Excel',
            'studyBet.modal.validateQuestion': 'Validate request?',
            'studyBet.modal.validateMessage': 'This request will be marked as Validated',
            'studyBet.modal.rejectQuestion': 'Reject request?',
            'studyBet.modal.rejectMessage': 'The request will be marked as Rejected.',
            'studyBet.modal.rejectReasonPrompt': 'You can add a reason',
            'studyBet.modal.rejectReasonPlaceholder': 'Rejection reason...',

            // Detail Modal
            'studyBet.details.information': 'Information',
            'studyBet.details.associatedReports': 'Associated Reports',
            'studyBet.details.comments': 'Comments',
            'studyBet.details.noComments': 'No comments',
            'studyBet.details.noReports': 'No reports',
            'studyBet.details.concernedBET': 'Concerned BET',
            'studyBet.details.creationDate': 'Creation Date',
            'studyBet.details.state': 'State',
            'studyBet.details.close': 'Close',
            'studyBet.details.addReport': 'Add Report',
            'studyBet.details.downloadReport': 'Download',
            'studyBet.details.reportSubmitted': 'Submitted on',

            // Progress Steps
            'studyBet.progress.step1': 'Pending',
            'studyBet.progress.step2': 'In Progress',
            'studyBet.progress.step3': 'Delivered',
            'studyBet.progress.step4': 'Validated',

            // ============ LOTS COMPONENT ============
            // Modal: New lot
            'lots.modal.designation': 'Lot Designation',
            'lots.modal.description': 'Description',
            'lots.modal.startDate': 'Expected Start Date',
            'lots.modal.endDate': 'Expected End Date',
            'lots.modal.assignSubcontractor': 'Assign Subcontractor',
            'lots.modal.update': 'Update',
            'lots.modal.subcontractorPlaceholder': 'Mrs. Sarr (Electricity Pro)',
            'lots.modal.designationPlaceholder': 'Structural Work',
            'lots.modal.descriptionPlaceholder': 'Foundations, masonry, reinforced concrete, framework',

            // Card labels
            'lots.card.expectedDate': 'Expected Date',
            'lots.card.expectedEndDate': 'Expected End',

            // Details modal
            'lots.details.noDocuments': 'No documents at the moment',
            'lots.details.addDocument': 'Add Document',
            'lots.details.documentsAttached': 'Attached Documents',



            'stock.newMaterial': 'New Material',
            'stock.newOrder': 'New Order',
            'stock.reset': 'Reset',
            'stock.modals.delete.message': 'Are you sure you want to delete this material?',
            'stock.modals.delete.cancel': 'Cancel',
            'stock.modals.delete.delete': 'Delete',

            // Filters
            'stock.filters.searchMaterial': 'Search by material, unit...',
            'stock.filters.searchOrder': 'Search by order, supplier',
            'stock.filters.searchDelivery': 'Search by delivery, supplier',
            'stock.filters.allMaterials': 'All Materials',
            'stock.filters.normalStock': 'Normal Stock',
            'stock.filters.lowStock': 'Low Stock',
            'stock.filters.outOfStock': 'Out of Stock',
            'stock.filters.allStatus': 'All Statuses',
            'stock.filters.allDeliveries': 'All Deliveries',

            // Tables
            'stock.search': 'Search for an item...',
            'stock.addArticle': 'Add item',
            'stock.articleName': 'Item name',
            'stock.category': 'Category',
            'stock.quantity': 'Quantity',
            'stock.unit': 'Unit',
            'stock.minStock': 'Minimum stock',
            'stock.currentStock': 'Current Stock',
            'stock.status': 'Status',
            'stock.actions': 'Actions',
            'stock.table.threshold': 'Threshold',
            'stock.table.material': 'Material',
            'stock.table.stock': 'Stock',
            'stock.table.unit': 'Unit',
            'stock.table.actions': 'Actions',
            'stock.table.state': 'State',
            'stock.actions.manageMovements': 'Manage Movement',
            'stock.actions.history': 'History',
            'stock.actions.delete': 'Delete',
            'stock.table.orderNumber': 'Order No.',
            'stock.table.deliveryNumber': 'Delivery No.',
            'stock.table.proof': 'Proof',

            // Status
            'stock.inStock': 'In stock',
            'stock.lowStock': 'Low stock',
            'stock.outOfStock': 'Out of stock',

            // Actions
            'stock.edit': 'Edit',
            'stock.delete': 'Delete',
            'stock.noArticles': 'No items in stock',
            'stock.addNewArticle': 'Add a new item',
            'stock.editArticle': 'Edit item',
            'stock.cancel': 'Cancel',
            'stock.save': 'Save',
            'stock.saving': 'Saving...',
            'stock.deleteConfirm': 'Are you sure you want to delete this item?',
            'stock.confirmDelete': 'Confirm deletion',

            // Right Column Cards

            'stock.movements.title': 'Recent Movements',
            'stock.movements.none': 'No recent movements',
            'stock.movements.entry': 'Entry',
            'stock.movements.exit': 'Exit',
            'stock.movements.adjustment': 'Adjustment',

            // Statistics
            'stock.stats.consumption': 'Monthly Consumption',
            'stock.stats.top5': 'Top 5 most used materials',
            'stock.stats.evolution': 'Monthly Evolution',

            // Modals

            'stock.modals.delete.irreversible': 'This action is irreversible',
            'stock.modals.success': 'Success!',
            'stock.modals.error': 'Error',

            'stock.time.justNow': 'Just now',
            'stock.time.minutesAgo': '{{min}} min ago',
            'stock.time.today': 'Today, {{time}}',
            'stock.time.yesterday': 'Yesterday, {{time}}',
            'stock.printingNotReady': 'Printing feature under development',
            'stock.errors.invalidPropertyId': 'Invalid Property ID',
            'stock.errors.loadStock': 'Error loading stock',
            'stock.errors.loadOrders': 'Error loading orders',
            'stock.errors.loadDeliveries': 'Error loading deliveries',

            'stock.deliveryStatus.inDelivery': 'In Delivery',
            'stock.deliveryStatus.delivered': 'Delivered',
            'stock.deliveryStatus.cancelled': 'Cancelled',
            'stock.deliveryStatus.pending': 'Pending',
            'stock.deliveryStatus.approved': 'Approved',
            'Tous les matériaux': 'All materials',
            'stock.allCategories': 'All categories',
            'stock.materials': 'Materials',
            'stock.tools': 'Tools',
            'stock.equipment': 'Equipment',
            'stock.consumables': 'Consumables',
            'stock.supplier': 'Supplier',
            'stock.unitPrice': 'Unit price',
            'stock.totalValue': 'Total value',
            'stock.lastUpdated': 'Last updated',
            'stock.addStock': 'Add stock',
            'stock.removeStock': 'Remove stock',
            'stock.movement': 'Movement',
            'stock.materialStockAlerts': 'Material stock alerts',
            'stock.recentMovements': 'Recent movements',
            'stock.movementHistory': 'Movement history',

            'stock.viewAll': 'View all',
            'stock.viewAllMovements': 'View all movements',


            // ============ LOTS (Complement) ============
            'lots.searchPlaceholder': 'Search by lot name or subcontractor...',
            'lots.allStatus': 'All statuses',
            'lots.progressPercentage': 'Progress percentage',
            'lots.preview': 'Preview',
            'lots.commentsTitle': 'Lot Comments',
            'lots.commentsHistory': 'Comments History',
            'lots.noComments': 'No comments',
            'lots.beFirstToComment': 'Be the first to comment on this lot',
            'lots.addComment': 'Add a comment',
            'lots.yourComment': 'Your comment',
            'lots.commentPlaceholder': 'Share your observations, questions or remarks...',
            'lots.detailsTitle': 'Lot Details',
            'lots.assignedSubcontractor': 'Assigned Subcontractor',
            'lots.attachedDocuments': 'Attached Documents',
            'lots.property': 'Property',
            'lots.subcontractorPlaceholder': 'Search for a subcontractor...',
            'lots.noSubcontractorFound': 'No subcontractor found',
            'lots.selectedSubcontractor': 'Selected Subcontractor',
            'lots.change': 'Change',
            'lots.updateProgress': 'Update Progress',
            'lots.validate': 'Validate',
            'lots.updating': 'Updating...',
            'lots.sending': 'Sending...',
            'lots.send': 'Send',
            'lots.documentName': 'Document Name',


            // Lots Status & Errors
            'lots.status.PENDING': 'Pending',
            'lots.status.IN_PROGRESS': 'In Progress',
            'lots.status.COMPLETED': 'Completed',
            'lots.error.missingPropertyId': 'Property ID missing in URL',
            'lots.error.sessionExpired': 'Session expired. Please login again.',
            'lots.error.fileTooBig': 'File is too large (max 10 MB)',
            'lots.error.noFileSelected': 'No file selected',
            'lots.error.documentLabelRequired': 'Document label is required',
            'lots.success.documentAdded': 'Document added successfully!',
            'lots.error.documentUploadFailed': 'Document upload failed',
            'lots.confirmDeleteDocument': 'Are you sure you want to delete this document?',
            'lots.success.documentDeleted': 'Document deleted successfully!',
            'lots.error.deleteDocumentFailed': 'Error deleting document',
            'lots.error.loadSubcontractors': 'Unable to load subcontractors',
            'lots.error.loadLots': 'Unable to load lots',
            'lots.error.percentageRange': 'Percentage must be between 0 and 100',
            'lots.success.progressUpdated': 'Progress updated successfully!',
            'lots.error.progressUpdateFailed': 'Error updating progress',
            'lots.success.statusChanged': 'Status changed successfully!',
            'lots.error.statusChangeFailed': 'Error changing status',
            'lots.error.lotNameRequired': 'Lot name is required',
            'lots.error.descriptionRequired': 'Description is required',
            'lots.error.startDateRequired': 'Start date is required',
            'lots.error.endDateRequired': 'End date is required',
            'lots.error.dateRangeInvalid': 'End date must be after start date',
            'lots.error.subcontractorRequired': 'Please select a subcontractor',
            'lots.success.lotCreated': 'Lot created successfully!',
            'lots.success.lotUpdated': 'Lot updated successfully!',
            'lots.error.serverConnect': 'Server connection error',
            'lots.error.invalidData': 'Invalid data',
            'lots.error.accessDenied': 'You do not have permission to perform this action',
            'lots.error.notFound': 'Property or subcontractor not found',
            'lots.error.serverError': 'Server error. Please try again later',
            'lots.error.saveFailed': 'An error occurred while saving',
            'lots.error.fileSizeLimit': 'File must not exceed 5MB',
            'lots.error.fileFormat': 'Unsupported format. Use PDF, JPG or PNG',

            // ============ PLAN SELECTION POPUP ============
            'popup.welcome.title': 'Welcome! Discover our plans to create your first project',
            'popup.welcome.subtitle': 'Choose the plan that best suits your needs',
            'popup.welcome.close': 'Close',
            'popup.plan.basic.name': 'Basic',
            'popup.plan.basic.description': 'Perfect to get started with essential features',
            'popup.plan.basic.feature1': 'Up to 3 projects',
            'popup.plan.basic.feature2': 'Basic task management',
            'popup.plan.basic.feature3': 'Standard support',
            'popup.plan.premium.name': 'Premium',
            'popup.plan.premium.description': 'For demanding professionals with all features',
            'popup.plan.premium.feature1': 'Unlimited projects',
            'popup.plan.premium.feature2': 'Advanced task management',
            'popup.plan.premium.feature3': 'Priority support 24/7',
            'popup.plan.premium.feature4': 'Detailed reports and analytics',
            'popup.plan.choosePlan': 'Choose this plan',
            'popup.plan.mostPopular': 'Most popular',
            'popup.billing.monthly': 'Monthly',
            'popup.billing.yearly': 'Yearly',
            'popup.billing.saveUpTo': 'Save up to',
            'popup.billing.perMonth': '/ month',
            'popup.billing.perMonthYearly': '/ month (yearly)',
            'popup.loading': 'Loading plans...',
            'popup.error.loadPlans': 'Error loading plans',
            'popup.error.payment': 'Error initializing payment',
            'popup.error.loginRequired': 'You must be logged in to subscribe',



            // ============ NEW TRANSLATIONS ============
            // Subcontractors
            'subcontractor.title': 'Subcontractors',
            'subcontractor.subtitle': 'Find all your subcontractors and partners at a glance.',
            'subcontractor.new': 'New Subcontractor',
            'subcontractor.searchPlaceholder': 'Search by name, email...',
            'subcontractor.filter': 'Filters',
            'subcontractor.export': 'Export',
            'subcontractor.delete': 'Delete',
            'subcontractor.table.companyName': 'COMPANY NAME',
            'subcontractor.table.contactName': 'CONTACT NAME',
            'subcontractor.table.phone': 'PHONE',
            'subcontractor.table.email': 'EMAIL',
            'subcontractor.table.status': 'STATUS',
            'subcontractor.status.active': 'Active',
            'subcontractor.status.inactive': 'Inactive',
            'subcontractor.modal.title': 'New Subcontractor',
            'subcontractor.modal.name': 'Last Name',
            'subcontractor.modal.firstName': 'First Name',
            'subcontractor.modal.phone': 'Phone',
            'subcontractor.modal.email': 'Email',
            'subcontractor.modal.password': 'Password',
            'subcontractor.modal.birthDate': 'Date of Birth',
            'subcontractor.modal.birthPlace': 'Place of Birth',
            'subcontractor.modal.address': 'Address',
            'subcontractor.modal.note': 'Note: This subcontractor will be automatically created with the "SUBCONTRACTOR" profile',
            'subcontractor.modal.cancel': 'Cancel',
            'subcontractor.modal.save': 'Save',
            'subcontractor.modal.saving': 'Saving...',
            'subcontractor.loading': 'Loading...',
            'subcontractor.success.add': 'Subcontractor added successfully!',
            'subcontractor.error.add': 'Error creating subcontractor',
            'subcontractor.success.delete': '{{count}} subcontractor(s) deleted successfully',
            'subcontractor.error.delete': '{{count}} error(s) during deletion',
            'subcontractor.confirmDelete': 'Are you sure you want to delete {{count}} subcontractor(s)?',
            'subcontractor.validation.required': 'Please fill in all required fields',
            'subcontractor.validation.email': 'Please enter a valid email address',
            'subcontractor.validation.phone': 'Please enter a valid phone number',
            'subcontractor.validation.password': 'Password must contain at least 6 characters',
            'subcontractor.validation.selection': 'Please select at least one subcontractor to delete',
            'subcontractor.pagination.viewing': 'Showing {{start}} to {{end}} of {{total}}',
            'subcontractor.pagination.pages': '({{total}} pages)',
            'subcontractor.export.success': 'Export successful!',

            // Suppliers
            'supplier.title': 'Suppliers',
            'supplier.subtitle': 'Management of your company Suppliers',
            'supplier.new': 'New Supplier',
            'supplier.searchPlaceholder': 'Search by name',
            'supplier.table.name': 'NAME',
            'supplier.table.phone': 'PHONE',
            'supplier.table.email': 'EMAIL',
            'supplier.table.position': 'POSITION',
            'supplier.table.status': 'STATUS',
            'supplier.modal.title': 'New Supplier',
            'supplier.filter': 'Filters',
            'supplier.export': 'Export',
            'supplier.delete': 'Delete',
            'supplier.status.active': 'Active',
            'supplier.status.inactive': 'Inactive',
            'supplier.modal.name': 'Last Name',
            'supplier.modal.firstName': 'First Name',
            'supplier.modal.phone': 'Phone',
            'supplier.modal.email': 'Email',
            'supplier.modal.password': 'Password',
            'supplier.modal.birthDate': 'Date of Birth',
            'supplier.modal.birthPlace': 'Place of Birth',
            'supplier.modal.address': 'Address',
            'supplier.modal.cancel': 'Cancel',
            'supplier.modal.save': 'Save',
            'supplier.modal.saving': 'Saving...',
            'supplier.loading': 'Loading...',
            'supplier.success.add': 'Supplier added successfully!',
            'supplier.error.add': 'Error creating supplier',
            'supplier.success.delete': '{{count}} supplier(s) deleted successfully',
            'supplier.error.delete': '{{count}} error(s) during deletion',
            'supplier.confirmDelete': 'Are you sure you want to delete {{count}} supplier(s)?',
            'supplier.validation.required': 'Please fill in all required fields',
            'supplier.validation.email': 'Please enter a valid email address',
            'supplier.validation.phone': 'Please enter a valid phone number',
            'supplier.validation.password': 'Password must contain at least 6 characters',
            'supplier.validation.selection': 'Please select at least one supplier to delete',
            'supplier.pagination.viewing': 'Showing {{start}} to {{end}} of {{total}}',
            'supplier.pagination.pages': '({{total}} pages)',
            'supplier.export.success': 'Export successful!',

            // Admin Subscriptions
            'admin.subscription.title': 'Subscription Plans',
            'admin.subscription.description': 'Create, edit, or deactivate subscription plans offered to users',
            'admin.subscription.create': 'Create Plan',
            'admin.subscription.table.name': 'Plan Name',
            'admin.subscription.table.cost': 'Total Cost',
            'admin.subscription.table.installments': 'Installments',
            'admin.subscription.table.projectLimit': 'Project Limit',
            'admin.subscription.table.discount': 'Yearly Discount',
            'admin.subscription.table.status': 'Status',
            'admin.subscription.table.actions': 'Actions',
            'admin.subscription.empty': 'No plans found',
            'admin.subscription.status.active': 'Active',
            'admin.subscription.status.inactive': 'Inactive',
            'admin.subscription.unlimited': 'Unlimited',
            'admin.subscription.pagination.showing': 'Showing',
            'admin.subscription.pagination.to': 'to',
            'admin.subscription.pagination.of': 'of',
            'admin.subscription.pagination.results': 'results',
            'admin.subscription.pagination.previous': 'Previous',
            'admin.subscription.pagination.next': 'Next',
            'admin.subscription.loading': 'Loading plans...',
            'admin.subscription.error.load': 'Error loading plans',
            'admin.subscription.success.delete': 'Plan "{{label}}" deleted successfully',
            'admin.subscription.error.delete': 'Error deleting plan',
            'admin.subscription.action.view': 'View',
            'admin.subscription.action.edit': 'Edit',
            'admin.subscription.searchPlaceholder': 'Search by name...',
            'admin.subscription.filter': 'Filters',
            'admin.subscription.export': 'Export',
            'admin.subscription.modal.delete.title': 'Confirm Deletion',
            'admin.subscription.modal.delete.message': 'Are you sure you want to delete the plan "{{plan}}"? This action is irreversible.',
            'admin.subscription.modal.delete.confirm': 'Delete',
            'admin.subscription.modal.delete.deleting': 'Deleting...',
            'admin.subscription.modal.toggle.deactivate.title': 'Deactivate Plan',
            'admin.subscription.modal.toggle.activate.title': 'Activate Plan',
            'admin.subscription.modal.toggle.deactivate.message': 'Are you sure you want to deactivate the plan {{plan}}? This action is irreversible and it will no longer be visible to users.',
            'admin.subscription.modal.toggle.activate.message': 'Are you sure you want to activate the plan {{plan}}? This action is irreversible and it will be visible to users.',
            'admin.subscription.notification.deactivated': 'Plan "{{plan}}" has been deactivated.',
            'admin.subscription.notification.activated': 'Plan "{{plan}}" has been activated.',


            'stock.modals.order.title': 'New Material Order',
            'stock.modals.order.supplier': 'Supplier',
            'stock.modals.order.selectSupplier': 'Select a supplier',
            'stock.modals.order.supplierRequired': 'Please select a supplier',
            'stock.modals.order.materialsOrdered': 'Ordered Materials',
            'stock.modals.order.addMaterial': 'Add Material',
            'stock.modals.order.material': 'Material',
            'stock.modals.order.currentStock': 'Current Stock',
            'stock.modals.order.quantity': 'Quantity',
            'stock.modals.order.selectMaterial': 'Select a material',
            'stock.modals.order.cancel': 'Cancel',
            'stock.modals.order.save': 'Save',
            'stock.modals.material.title': 'Add New Material',
            'stock.modals.material.name': 'Material Name',
            'stock.modals.material.currentQuantity': 'Current Quantity',
            'stock.modals.material.criticalThreshold': 'Critical Threshold',
            'stock.modals.material.unit': 'Unit',
            'stock.modals.material.property': 'Property',
            'stock.modals.material.loadingUnits': 'Loading units...',
            'stock.modals.material.loadingProperties': 'Loading properties...',
            'stock.modals.material.required': 'This field is required',
            'stock.modals.material.saving': 'Saving...',
            'stock.modals.movement.title': 'Stock Movement',
            'stock.modals.movement.type': 'Movement Type',
            'stock.modals.movement.entry': 'Entry',
            'stock.modals.movement.exit': 'Exit',
            'stock.modals.movement.adjustment': 'Adjustment',
            'stock.modals.movement.selectType': 'Please select a type',
            'stock.modals.movement.enterQuantity': 'Enter quantity',
            'stock.modals.movement.quantityError': 'Quantity must be greater than 0',
            'stock.modals.movement.reference': 'Reference',
            'stock.modals.movement.referenceRequired': 'Reference is required',
            'stock.modals.delete.title': 'Confirm Deletion',
            'stock.modals.delete.materialMessage': 'Are you sure you want to delete {{label}}?',
            'stock.modals.delete.orderMessage': 'Are you sure you want to delete order {{id}}?',
            'stock.modals.delete.warning': 'This action is irreversible and will also delete associated movement history.',
            'stock.modals.delete.confirm': 'Delete',
            'stock.modals.delete.deleting': 'Deleting...',
            'stock.alerts.title': 'Stock Alerts',
            'stock.alerts.none': 'No alerts at the moment',
            'stock.charts.consumption': 'Monthly Consumption',
            'stock.charts.top5': 'Top 5 Most Used Materials',
            'stock.charts.evolution': 'Monthly Evolution',
            'stock.actions.viewDetails': 'View Details',
            'stock.actions.edit': 'Edit',

            'stock.actions.downloadProof': 'Download Proof',
            'stock.orderStatus.pending': 'Pending',
            'stock.orderStatus.confirmed': 'Confirmed',
            'stock.orderStatus.inProgress': 'In Progress',
            'stock.orderStatus.delivery': 'Delivered',
            'stock.orderStatus.delivered': 'Delivered',
            'stock.orderStatus.cancelled': 'Cancelled',
            'stock.orderStatus.approved': 'Approved',
            'stock.orderStatus.partiallyDelivered': 'Partially Delivered',
            'stock.success.materialDeleted': '{{name}} deleted successfully',
            'stock.error.accessDenied': 'Access denied - You do not have necessary permissions',
            'stock.error.deleteConflict': 'Cannot delete this material as it is linked to orders',
            'stock.error.deleteFailed': 'Error deleting material',
            'stock.error.cannotEditOrder': 'Cannot edit a delivered or cancelled order',
            'stock.error.cannotCancelOrder': 'Cannot cancel an order that is already delivered or cancelled',
            'stock.error.noProof': 'No proof available for this delivery',

            // Incidents / Etude-BET (Ajouts)
            'incidents.status': 'Status',
            'incidents.statusUpdated': 'Status updated successfully',
            'incidents.statusUpdateError': 'Error updating status',
            'incidents.statusPending': 'Pending',
            'incidents.statusOpen': 'Open',
            'incidents.statusInProgress': 'In progress',
            'incidents.statusResolved': 'Resolved',
            'incidents.statusClosed': 'Closed',
            'studyBet.modal.objective': 'Objective',
            'studyBet.modal.objectivePlaceholder': 'Describe the study objective...',
            'studyBet.modal.problemObserved': 'Problem observed',
            'studyBet.modal.problemObservedPlaceholder': 'Describe the observed problem...',
            'studyBet.modal.studyType': "Study type",
            'studyBet.modal.selectType': "Select a type",
            'studyBet.modal.attachedDocuments': "Attached documents",
            'studyBet.modal.addDocument': "Add a document",
            'studyBet.modal.type': "Type",
            'studyBet.modal.noDocumentAdded': "No document added. Click on Add a document to attach one.",

            // IA Report Modal
            'studyBet.ia.title': 'AI Report',
            'studyBet.ia.loading': 'Loading AI report...',
            'studyBet.ia.errorRetry': 'Please try again later.',
            'studyBet.ia.severity': 'Severity',
            'studyBet.ia.observations': 'Observations',
            'studyBet.ia.recommendations': 'Recommendations',
            'studyBet.ia.conclusion': 'Conclusion',
            'studyBet.ia.analyzedDocs': 'Analyzed documents',
            'studyBet.ia.document': 'AI Document',

            // ============ ATTENDANCE REPORT ============
            'attendanceReport.title': 'Attendance Report',
            'attendanceReport.loading': 'Loading report...',
            'attendanceReport.retry': 'Retry',
            'attendanceReport.noData': 'No workers found',
            'attendanceReport.noDataHint': 'No attendance data available for this period.',
            'attendanceReport.exportExcel': 'Export Excel',
            'attendanceReport.period': 'Period',
            'attendanceReport.totalWorkingDays': 'Total working days',
            'attendanceReport.pastWorkingDays': 'Past working days',
            'attendanceReport.remainingWorkingDays': 'Remaining days',
            'attendanceReport.workersTable': 'Worker details',
            'attendanceReport.fullName': 'Full name',
            'attendanceReport.daysPresent': 'Days present',
            'attendanceReport.leaveDays': 'Leave days',
            'attendanceReport.absentDays': 'Absent days',
            'attendanceReport.salaryDeduction': 'Salary deduction',
            'attendanceReport.presence': 'Presence',
            'attendanceReport.legendGood': 'Good',
            'attendanceReport.legendWarning': 'Warning',
            'attendanceReport.legendCritical': 'Critical',
        }
    };

    constructor() {
        const savedLang = localStorage.getItem('language') as Language;
        if (savedLang && (savedLang === 'FR' || savedLang === 'EN')) {
            this.currentLang.set(savedLang);
        }
    }

    changeLanguage(lang: Language) {
        this.currentLang.set(lang);
        localStorage.setItem('language', lang);
    }

    translate(key: string, params?: { [key: string]: string | number }): string {
        const lang = this.currentLang();
        let text = this.translations[lang][key] || key;

        if (params) {
            Object.keys(params).forEach(param => {
                text = text.replace(`{{${param}}}`, String(params[param]));
            });
        }

        return text;
    }
}
